import React, { useState, useEffect, useCallback } from 'react';
import {
  adminLogin,
  adminVerify2FA,
  adminLogout,
  checkAdminAuth,
  getAdminDashboard,
  adminCreateEvent,
  adminUpdateEventStatus,
  adminUpdateEventDate,
  adminUpdateFightResult,
  adminUpdateFight,
  adminDeleteEvent,
  adminDeleteFight,
  adminImportEvent,
  adminRefreshEvent,
  adminUpdateSourceUrl,
  adminUpdateUfcStatsUrl,
  adminAutoDetectStatsUrl,
  adminScrapeResults,
  getEvent,
  getFights,
  getAdminPoolsData,
  getAdminPool,
  getAdminLeaderboards,
  adminDeleteUser,
  adminSetup2FA,
  adminVerify2FASetup,
  adminDisable2FA,
  adminRepairPicks,
  adminFixEarlyPrelims,
  adminFetchOdds,
  getAdminPoolStandings,
  getAdminActivityLogs,
  adminListDuplicateEvents,
  adminMergeDuplicateEvents,
  getAdminChatPools,
  getAdminChatMessages,
  adminSendNotification,
  adminGetNotificationLogs,
  adminGetScheduledNotifications,
  adminCreateScheduledNotification,
  adminUpdateScheduledNotification,
  adminDeleteScheduledNotification
} from '../services/api';
import Loading from '../components/Loading';
import UserHoverCard from '../components/UserHoverCard';

// Auto-refresh interval for live events (30 seconds)
const LIVE_REFRESH_INTERVAL = 30000;

const METHODS = ['KO/TKO', 'Submission', 'Decision'];

function Admin() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loginError, setLoginError] = useState('');

  // 2FA state
  const [requires2FA, setRequires2FA] = useState(false);
  const [pendingToken, setPendingToken] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);

  const [dashboard, setDashboard] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [fights, setFights] = useState([]);
  const [activeTab, setActiveTab] = useState('cards');
  const [showCreateEventForm, setShowCreateEventForm] = useState(false);

  const [expandedFights, setExpandedFights] = useState({});

  const toggleFight = (fightId) => {
    setExpandedFights(prev => ({ ...prev, [fightId]: !prev[fightId] }));
  };

  // Form states
  const [newEvent, setNewEvent] = useState({ name: '', date: '', venue: '' });
  const [importing, setImporting] = useState(false);
  const [refreshingCard, setRefreshingCard] = useState(false);
  const [repairingPicks, setRepairingPicks] = useState(false);
  const [fixingEarlyPrelims, setFixingEarlyPrelims] = useState(false);
  const [editingSourceUrl, setEditingSourceUrl] = useState('');
  const [savingSourceUrl, setSavingSourceUrl] = useState(false);
  const [editingUfcStatsUrl, setEditingUfcStatsUrl] = useState('');
  const [savingUfcStatsUrl, setSavingUfcStatsUrl] = useState(false);
  const [scrapingResults, setScrapingResults] = useState(false);
  const [scrapeMessage, setScrapeMessage] = useState(null);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [lastUpdated, setLastUpdated] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(true);

  // Data explorer state
  const [poolsData, setPoolsData] = useState(null);
  const [poolsDataLoading, setPoolsDataLoading] = useState(false);
  const [selectedPoolFilter, setSelectedPoolFilter] = useState('all-users');
  const [poolCountFilter, setPoolCountFilter] = useState('any');
  const [joinedDateFilter, setJoinedDateFilter] = useState('all-time');
  const [searchTerm, setSearchTerm] = useState('');
  const [userSortColumn, setUserSortColumn] = useState('joined');
  const [userSortDirection, setUserSortDirection] = useState('desc');
  const [selectedUserIds, setSelectedUserIds] = useState(new Set());

  // 2FA settings state
  const [setup2FAData, setSetup2FAData] = useState(null);
  const [setup2FACode, setSetup2FACode] = useState('');
  const [disable2FAPassword, setDisable2FAPassword] = useState('');
  const [disable2FACode, setDisable2FACode] = useState('');
  const [twoFALoading, setTwoFALoading] = useState(false);

  // Event date editing state
  const [editingEventDate, setEditingEventDate] = useState(false);
  const [eventDateInput, setEventDateInput] = useState('');

  // Pools tab state
  const [selectedAdminPool, setSelectedAdminPool] = useState(null);
  const [adminPoolLoading, setAdminPoolLoading] = useState(false);
  const [adminPoolsList, setAdminPoolsList] = useState([]);
  const [poolsSortField, setPoolsSortField] = useState('created_at');
  const [poolsSortDirection, setPoolsSortDirection] = useState('desc');
  const [selectedPoolIds, setSelectedPoolIds] = useState(new Set());
  const [emailsCopied, setEmailsCopied] = useState(false);
  const [poolAllTimeLeaders, setPoolAllTimeLeaders] = useState([]);
  const [poolsEventList, setPoolsEventList] = useState([]);
  const [poolsActiveEvent, setPoolsActiveEvent] = useState('');

  // Leaderboard tab state
  const [leaderboardData, setLeaderboardData] = useState(null);
  const [leaderboardLoading, setLeaderboardLoading] = useState(false);

  // Activity tab state
  const [activityLogs, setActivityLogs] = useState([]);
  const [duplicateGroups, setDuplicateGroups] = useState([]);
  const [duplicatesLoading, setDuplicatesLoading] = useState(false);
  const [merging, setMerging] = useState(false);
  const [activityStats, setActivityStats] = useState(null);
  const [activityLoading, setActivityLoading] = useState(false);
  const [activityFilter, setActivityFilter] = useState('all');

  // Chat review tab state (read-only)
  const [chatPools, setChatPools] = useState([]);
  const [chatPoolsLoading, setChatPoolsLoading] = useState(false);
  const [chatPoolFilter, setChatPoolFilter] = useState('');
  const [selectedChatPool, setSelectedChatPool] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatMessagesLoading, setChatMessagesLoading] = useState(false);

  // Notifications tab state
  const [notifSubTab, setNotifSubTab] = useState('send'); // 'send' | 'scheduled' | 'logs'
  const [notifTitle, setNotifTitle] = useState('');
  const [notifBody, setNotifBody] = useState('');
  const [notifAudienceType, setNotifAudienceType] = useState('all');
  const [notifAudiencePoolId, setNotifAudiencePoolId] = useState('');
  const [notifSending, setNotifSending] = useState(false);
  const [notificationLogs, setNotificationLogs] = useState([]);
  const [notificationLogsLoading, setNotificationLogsLoading] = useState(false);
  const [scheduledNotifications, setScheduledNotifications] = useState([]);
  const [scheduledLoading, setScheduledLoading] = useState(false);
  const [showScheduleForm, setShowScheduleForm] = useState(false);
  const [scheduleForm, setScheduleForm] = useState({
    title: '', body: '', audienceType: 'all', audiencePoolId: '',
    triggerType: 'daily', triggerTime: '09:00', triggerDay: 0, hoursBeforeLock: 2,
  });
  const [scheduleSaving, setScheduleSaving] = useState(false);

  useEffect(() => {
    checkAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-refresh for live events
  const refreshEventData = useCallback(async (eventId) => {
    if (!eventId) return;
    setIsRefreshing(true);
    try {
      const [eventResponse, fightsResponse] = await Promise.all([
        getEvent(eventId),
        getFights(eventId)
      ]);
      setSelectedEvent(eventResponse.data);
      setFights(fightsResponse.data || []);
      setLastUpdated(new Date());
    } catch (err) {
      console.error('Auto-refresh failed:', err);
    } finally {
      setIsRefreshing(false);
    }
  }, []);

  // Auto-refresh interval when event is locked (live)
  useEffect(() => {
    if (selectedEvent?.status === 'locked' && autoRefreshEnabled && selectedEvent?.id) {
      const eventId = selectedEvent.id;
      const interval = setInterval(() => refreshEventData(eventId), LIVE_REFRESH_INTERVAL);
      return () => clearInterval(interval);
    }
  }, [selectedEvent?.status, selectedEvent?.id, autoRefreshEnabled, refreshEventData]);

  // Auto-load event details if dashboard has event but selectedEvent is null
  // Auto-expand the most relevant event the FIRST time the dashboard loads.
  // Don't re-fire when the user explicitly collapses a card.
  const [hasAutoExpanded, setHasAutoExpanded] = useState(false);
  useEffect(() => {
    if (dashboard && !selectedEvent && !hasAutoExpanded) {
      const eventId = dashboard.currentEvent?.event?.id || dashboard.allEvents?.[0]?.id;
      if (eventId) {
        loadEventDetails(eventId);
      }
      setHasAutoExpanded(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashboard, selectedEvent, hasAutoExpanded]);

  const checkAuth = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      if (token) {
        // Verify token is still valid on server before setting authenticated
        const authResponse = await checkAdminAuth();
        setIsAuthenticated(true);
        setTwoFactorEnabled(!!authResponse.data.twoFactorEnabled);
        // Load dashboard after auth is confirmed
        await loadDashboard();
      }
    } catch (err) {
      // Silently clear stale token - don't show error on login form
      // This is expected when server restarts and clears in-memory sessions
      console.log('Session expired, please log in again');
      localStorage.removeItem('adminToken');
      setIsAuthenticated(false);
      // Don't set loginError here - only show errors for actual login attempts
    } finally {
      setLoading(false);
    }
  };

  const loadDashboard = async () => {
    try {
      const response = await getAdminDashboard();
      console.log('Dashboard response:', response.data);
      setDashboard(response.data);
      // Auto-select the current event or the first event in the list
      const eventId = response.data.currentEvent?.event?.id || response.data.allEvents?.[0]?.id;
      console.log('Auto-selecting event ID:', eventId);
      if (eventId) {
        await loadEventDetails(eventId);
      }
    } catch (err) {
      console.error('Failed to load dashboard:', err);
      // If unauthorized, clear token and throw to let checkAuth handle it
      if (err.response?.status === 401) {
        localStorage.removeItem('adminToken');
        setIsAuthenticated(false);
        throw err; // Re-throw so checkAuth catch block handles cleanup
      } else {
        showMessage('error', 'Failed to load dashboard');
      }
    }
  };

  const loadEventDetails = async (eventId) => {
    console.log('Loading event details for ID:', eventId);
    try {
      const [eventResponse, fightsResponse] = await Promise.all([
        getEvent(eventId),
        getFights(eventId)
      ]);
      console.log('Event response:', eventResponse.data);
      console.log('Fights response:', fightsResponse.data);
      setSelectedEvent(eventResponse.data);
      setFights(fightsResponse.data || []);
      setLastUpdated(new Date());
      setEditingUfcStatsUrl(eventResponse.data.ufc_stats_url || '');
      setScrapeMessage(null);
    } catch (err) {
      console.error('Failed to load event details:', err);
      showMessage('error', 'Failed to load event details');
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');

    try {
      const response = await adminLogin(username, password);

      // Check if 2FA is required
      if (response.data.requires2FA) {
        setRequires2FA(true);
        setPendingToken(response.data.pendingToken);
        setTotpCode('');
        return;
      }

      // No 2FA - complete login
      localStorage.setItem('adminToken', response.data.token);
      setIsAuthenticated(true);
      setTwoFactorEnabled(false);
      // Load dashboard separately - errors here shouldn't show as login errors
      try {
        await loadDashboard();
      } catch (dashErr) {
        // Dashboard load failed but login succeeded - already authenticated
        console.error('Dashboard load failed after login:', dashErr);
      }
    } catch (err) {
      setLoginError(err.response?.data?.error || 'Login failed');
    }
  };

  const handleVerify2FA = async (e) => {
    e.preventDefault();
    setLoginError('');

    try {
      const response = await adminVerify2FA(pendingToken, totpCode);
      localStorage.setItem('adminToken', response.data.token);
      setIsAuthenticated(true);
      setRequires2FA(false);
      setPendingToken('');
      setTotpCode('');
      setTwoFactorEnabled(true);
      // Load dashboard
      try {
        await loadDashboard();
      } catch (dashErr) {
        console.error('Dashboard load failed after login:', dashErr);
      }
    } catch (err) {
      setLoginError(err.response?.data?.error || 'Invalid verification code');
    }
  };

  const handleBack2FA = () => {
    setRequires2FA(false);
    setPendingToken('');
    setTotpCode('');
    setLoginError('');
    setPassword('');
  };

  const handleLogout = async () => {
    try {
      await adminLogout();
    } catch (err) {
      // Ignore logout errors
    }
    localStorage.removeItem('adminToken');
    setIsAuthenticated(false);
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  };

  // Helper to format UTC date as CST for display
  const formatDateCST = (utcDateString) => {
    if (!utcDateString) return '';
    const date = new Date(utcDateString);
    return date.toLocaleString('en-US', {
      timeZone: 'America/Chicago',
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    }) + ' CST';
  };

  // Relative-time helper used by Activity, Chat Review, and other admin lists
  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Helper to convert datetime-local input (treated as CST) to UTC ISO string
  const cstToUtc = (localDatetimeString) => {
    // Create a date in CST by temporarily adjusting
    // datetime-local gives us "2026-01-25T20:00" - we want to treat this as 8pm CST
    const [datePart, timePart] = localDatetimeString.split('T');
    const [year, month, day] = datePart.split('-').map(Number);
    const [hours, minutes] = timePart.split(':').map(Number);

    // Create date as if it's CST, then convert to UTC
    // CST is UTC-6
    const utcHours = hours + 6;
    const date = new Date(Date.UTC(year, month - 1, day, utcHours, minutes, 0));
    return date.toISOString();
  };

  // Helper to get datetime-local value from UTC (converts to CST for input)
  const utcToDatetimeLocal = (utcDateString) => {
    if (!utcDateString) return '';
    const date = new Date(utcDateString);
    // Get CST time components
    const cstDate = new Date(date.toLocaleString('en-US', { timeZone: 'America/Chicago' }));
    const year = cstDate.getFullYear();
    const month = String(cstDate.getMonth() + 1).padStart(2, '0');
    const day = String(cstDate.getDate()).padStart(2, '0');
    const hours = String(cstDate.getHours()).padStart(2, '0');
    const mins = String(cstDate.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${mins}`;
  };

  const handleUpdateEventDate = async () => {
    if (!selectedEvent || !eventDateInput) return;
    try {
      const utcDate = cstToUtc(eventDateInput);
      await adminUpdateEventDate(selectedEvent.id, utcDate);
      showMessage('success', 'Event date updated successfully');
      setEditingEventDate(false);
      await loadDashboard();
    } catch (err) {
      showMessage('error', err.response?.data?.error || 'Failed to update event date');
    }
  };

  const handleCreateEvent = async (e) => {
    e.preventDefault();
    try {
      await adminCreateEvent(newEvent);
      showMessage('success', 'Event created successfully');
      setNewEvent({ name: '', date: '', venue: '' });
      await loadDashboard();
    } catch (err) {
      showMessage('error', err.response?.data?.error || 'Failed to create event');
    }
  };

  const handleUpdateStatus = async (eventId, status) => {
    try {
      await adminUpdateEventStatus(eventId, status);
      showMessage('success', `Event status updated to ${status}`);
      await loadDashboard();
    } catch (err) {
      showMessage('error', err.response?.data?.error || 'Failed to update status');
    }
  };

  const handleImportAllUpcoming = async () => {
    if (importing) return;
    if (!window.confirm('Scrape ufc.com for upcoming events and import any that are missing? Existing events will be left alone (their source URLs will be backfilled if missing).')) return;
    setImporting(true);
    try {
      const response = await adminImportEvent({ eventUrl: '', mainCardOnly: false });
      const ev = response.data?.imported_events ?? 0;
      const fi = response.data?.imported_fights ?? 0;
      const sk = response.data?.skipped ?? 0;
      showMessage('success', `Import complete. ${ev} event${ev === 1 ? '' : 's'}, ${fi} fight${fi === 1 ? '' : 's'} added. Skipped ${sk}.`);
      await loadDashboard();
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem('adminToken');
        setIsAuthenticated(false);
      } else {
        showMessage('error', err.response?.data?.error || 'Failed to import upcoming events');
      }
    } finally {
      setImporting(false);
    }
  };

  const handleRefreshCard = async () => {
    if (!selectedEvent) {
      showMessage('error', 'No event selected.');
      return;
    }
    if (!selectedEvent.source_url) {
      showMessage('error', 'No source URL set for this event. Please set the UFC event URL first.');
      return;
    }
    setRefreshingCard(true);
    try {
      const response = await adminRefreshEvent(selectedEvent.id);
      const totalFights = response.data?.total_fights ?? 0;
      const changes = response.data?.changes || {};

      // Build a detailed message about what changed
      const changeParts = [];
      if (changes.new_fights_added > 0) {
        changeParts.push(`${changes.new_fights_added} new fight${changes.new_fights_added === 1 ? '' : 's'} added`);
      }
      if (changes.fights_updated > 0) {
        changeParts.push(`${changes.fights_updated} fight${changes.fights_updated === 1 ? '' : 's'} updated`);
      }
      if (changes.fights_marked_cancelled > 0) {
        changeParts.push(`${changes.fights_marked_cancelled} fight${changes.fights_marked_cancelled === 1 ? '' : 's'} cancelled`);
      }
      if (changes.images_updated > 0) {
        changeParts.push(`${changes.images_updated} image${changes.images_updated === 1 ? '' : 's'} updated`);
      }

      let message;
      const activeFights = response.data?.active_fights ?? totalFights;
      const cancelledFights = changes.cancelled_fights || 0;
      const fightsSummary = cancelledFights > 0
        ? `${activeFights} active, ${cancelledFights} cancelled`
        : `${totalFights} fights`;

      if (changeParts.length > 0) {
        message = `Refreshed: ${changeParts.join(', ')}. Total: ${fightsSummary}.`;
      } else {
        message = `Card is up to date. Total: ${fightsSummary} (${changes.images_after || 0} with images).`;
      }
      showMessage('success', message);
      await loadEventDetails(selectedEvent.id);
    } catch (err) {
      console.error('Failed to refresh card:', err);
      showMessage('error', err.response?.data?.error || 'Failed to refresh card');
    } finally {
      setRefreshingCard(false);
    }
  };

  const handleRepairPicks = async () => {
    if (!selectedEvent) {
      showMessage('error', 'No event selected.');
      return;
    }
    setRepairingPicks(true);
    try {
      const response = await adminRepairPicks(selectedEvent.id);
      const { repaired, failed } = response.data;
      if (repaired > 0) {
        showMessage('success', `Repaired ${repaired} picks! ${failed > 0 ? `${failed} could not be repaired.` : ''}`);
      } else if (failed > 0) {
        showMessage('error', `Could not repair ${failed} picks. No orphaned picks could be matched.`);
      } else {
        showMessage('success', 'All picks are already valid. No repairs needed.');
      }
    } catch (err) {
      console.error('Failed to repair picks:', err);
      showMessage('error', err.response?.data?.error || 'Failed to repair picks');
    } finally {
      setRepairingPicks(false);
    }
  };

  const handleFixEarlyPrelims = async () => {
    if (!selectedEvent) {
      showMessage('error', 'No event selected.');
      return;
    }
    setFixingEarlyPrelims(true);
    try {
      const response = await adminFixEarlyPrelims(selectedEvent.id, 3);
      showMessage('success', `Fixed early prelims: ${response.data.fights.join(', ')}`);
      await loadEventDetails(selectedEvent.id);
    } catch (err) {
      console.error('Failed to fix early prelims:', err);
      showMessage('error', err.response?.data?.error || 'Failed to fix early prelims');
    } finally {
      setFixingEarlyPrelims(false);
    }
  };

  const [fetchingOdds, setFetchingOdds] = useState(false);
  const handleFetchOdds = async () => {
    if (!selectedEvent) return;
    setFetchingOdds(true);
    try {
      const response = await adminFetchOdds(selectedEvent.id);
      showMessage('success', `Odds updated: ${response.data.matched}/${response.data.total} fights matched from BestFightOdds`);
      await loadEventDetails(selectedEvent.id);
    } catch (err) {
      console.error('Failed to fetch odds:', err);
      showMessage('error', err.response?.data?.error || 'Failed to fetch odds');
    } finally {
      setFetchingOdds(false);
    }
  };

  const handleSaveSourceUrl = async () => {
    if (!selectedEvent || !editingSourceUrl.trim()) return;
    setSavingSourceUrl(true);
    try {
      await adminUpdateSourceUrl(selectedEvent.id, editingSourceUrl.trim());
      showMessage('success', 'Source URL saved for this event.');
      await loadEventDetails(selectedEvent.id);
      setEditingSourceUrl('');
    } catch (err) {
      console.error('Failed to save source URL:', err);
      showMessage('error', err.response?.data?.error || 'Failed to save source URL');
    } finally {
      setSavingSourceUrl(false);
    }
  };

  const [detectingStatsUrl, setDetectingStatsUrl] = useState(false);

  const handleAutoDetectStatsUrl = async () => {
    if (!selectedEvent) return;
    setDetectingStatsUrl(true);
    try {
      const res = await adminAutoDetectStatsUrl(selectedEvent.id);
      if (res.data.success) {
        setEditingUfcStatsUrl(res.data.url);
        showMessage('success', `Found UFC Stats URL: ${res.data.url}`);
        await loadEventDetails(selectedEvent.id);
      } else {
        showMessage('error', res.data.message || 'Could not find matching UFC Stats page');
      }
    } catch (err) {
      showMessage('error', err.response?.data?.error || 'Failed to auto-detect URL');
    } finally {
      setDetectingStatsUrl(false);
    }
  };

  const handleSaveUfcStatsUrl = async () => {
    if (!selectedEvent) return;
    setSavingUfcStatsUrl(true);
    try {
      await adminUpdateUfcStatsUrl(selectedEvent.id, editingUfcStatsUrl.trim() || null);
      showMessage('success', 'UFC Stats URL saved. Results will auto-scrape during the event.');
      await loadEventDetails(selectedEvent.id);
    } catch (err) {
      console.error('Failed to save UFC Stats URL:', err);
      showMessage('error', err.response?.data?.error || 'Failed to save UFC Stats URL');
    } finally {
      setSavingUfcStatsUrl(false);
    }
  };

  const handleScrapeResults = async () => {
    if (!selectedEvent) return;
    setScrapingResults(true);
    setScrapeMessage(null);
    try {
      const response = await adminScrapeResults(selectedEvent.id);
      const { scraped, updated, notFound, noResult } = response.data;
      setScrapeMessage({
        type: 'success',
        text: `Scraped ${scraped} fights, updated ${updated} results.${notFound?.length ? ` Not found: ${notFound.join(', ')}` : ''}${noResult?.length ? ` No result yet: ${noResult.length} fights` : ''}`
      });
      await loadEventDetails(selectedEvent.id);
    } catch (err) {
      console.error('Failed to scrape results:', err);
      setScrapeMessage({
        type: 'error',
        text: err.response?.data?.error || 'Failed to scrape results from UFC Stats'
      });
    } finally {
      setScrapingResults(false);
    }
  };

  const handleToggleCancelFight = async (fight) => {
    try {
      await adminUpdateFight(fight.id, { is_cancelled: fight.is_cancelled ? 0 : 1 });
      showMessage('success', fight.is_cancelled ? 'Fight restored.' : 'Fight cancelled.');
      if (selectedEvent) {
        await loadEventDetails(selectedEvent.id);
      }
    } catch (err) {
      showMessage('error', err.response?.data?.error || 'Failed to update fight');
    }
  };

  const handleUpdateFightDetails = async (fightId, data) => {
    try {
      await adminUpdateFight(fightId, data);
      showMessage('success', 'Fight updated.');
      if (selectedEvent) {
        await loadEventDetails(selectedEvent.id);
      }
    } catch (err) {
      showMessage('error', err.response?.data?.error || 'Failed to update fight');
    }
  };

  const handleUpdateResult = async (fightId, winner, method) => {
    try {
      await adminUpdateFightResult(fightId, winner, method);
      showMessage('success', 'Result updated');
      await loadEventDetails(selectedEvent.id);
    } catch (err) {
      showMessage('error', err.response?.data?.error || 'Failed to update result');
    }
  };

  const handleDeleteFight = async (fightId) => {
    if (!window.confirm('Are you sure you want to delete this fight?')) return;

    try {
      await adminDeleteFight(fightId);
      showMessage('success', 'Fight deleted');
      await loadEventDetails(selectedEvent.id);
    } catch (err) {
      showMessage('error', 'Failed to delete fight');
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm('Are you sure you want to delete this event? This will also delete all fights.')) return;

    try {
      await adminDeleteEvent(eventId);
      showMessage('success', 'Event deleted');
      setSelectedEvent(null);
      setFights([]);
      await loadDashboard();
    } catch (err) {
      showMessage('error', 'Failed to delete event');
    }
  };

  const loadPoolsData = async () => {
    setPoolsDataLoading(true);
    try {
      const response = await getAdminPoolsData();
      setPoolsData(response.data);
    } catch (err) {
      console.error('Failed to load pools data:', err);
      showMessage('error', 'Failed to load pools data');
    } finally {
      setPoolsDataLoading(false);
    }
  };

  // Load pools list for Pools tab
  const loadPoolsList = async (eventId) => {
    setAdminPoolLoading(true);
    try {
      const response = await getAdminPoolsData(eventId || poolsActiveEvent || undefined);
      setAdminPoolsList(response.data.pools || []);
      if (response.data.events) {
        setPoolsEventList(response.data.events || []);
      }
      if (!eventId && !poolsActiveEvent && response.data.activeEventId) {
        setPoolsActiveEvent(String(response.data.activeEventId));
      }
    } catch (err) {
      console.error('Failed to load pools list:', err);
      showMessage('error', 'Failed to load pools');
    } finally {
      setAdminPoolLoading(false);
    }
  };

  const handlePoolsEventChange = (eventId) => {
    setPoolsActiveEvent(eventId);
    loadPoolsList(eventId);
  };

  // Sort pools list
  const handlePoolsSort = (field) => {
    if (poolsSortField === field) {
      setPoolsSortDirection(poolsSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setPoolsSortField(field);
      setPoolsSortDirection('desc');
    }
  };

  const getSortedPoolsList = () => {
    return [...adminPoolsList].sort((a, b) => {
      let aVal = a[poolsSortField];
      let bVal = b[poolsSortField];

      // Handle special fields
      if (poolsSortField === 'member_count' || poolsSortField === 'entry_fee' || poolsSortField === 'active_users') {
        aVal = Number(aVal) || 0;
        bVal = Number(bVal) || 0;
      } else if (poolsSortField === 'created_at') {
        aVal = new Date(aVal || 0).getTime();
        bVal = new Date(bVal || 0).getTime();
      } else {
        aVal = (aVal || '').toString().toLowerCase();
        bVal = (bVal || '').toString().toLowerCase();
      }

      if (aVal < bVal) return poolsSortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return poolsSortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  };

  // Pool selection for commissioner emails
  const togglePoolSelection = (poolId) => {
    setSelectedPoolIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(poolId)) {
        newSet.delete(poolId);
      } else {
        newSet.add(poolId);
      }
      return newSet;
    });
    setEmailsCopied(false);
  };

  const toggleAllPoolsSelection = () => {
    if (selectedPoolIds.size === adminPoolsList.length) {
      setSelectedPoolIds(new Set());
    } else {
      setSelectedPoolIds(new Set(adminPoolsList.map(p => p.id)));
    }
    setEmailsCopied(false);
  };

  const getSelectedCommissionerEmails = () => {
    const emails = adminPoolsList
      .filter(pool => selectedPoolIds.has(pool.id) && pool.commissioner_email)
      .map(pool => pool.commissioner_email);
    // Remove duplicates
    return [...new Set(emails)];
  };

  const copySelectedEmails = async () => {
    const emails = getSelectedCommissionerEmails();
    if (emails.length === 0) return;
    try {
      await navigator.clipboard.writeText(emails.join(', '));
      setEmailsCopied(true);
      setTimeout(() => setEmailsCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy emails:', err);
    }
  };

  // Load single pool detail
  const loadPoolDetail = async (poolId) => {
    setAdminPoolLoading(true);
    setPoolAllTimeLeaders([]);
    try {
      const [poolRes, standingsRes] = await Promise.all([
        getAdminPool(poolId),
        getAdminPoolStandings(poolId).catch(() => ({ data: [] }))
      ]);
      setSelectedAdminPool(poolRes.data);
      setPoolAllTimeLeaders(standingsRes.data?.slice(0, 3) || []);
    } catch (err) {
      console.error('Failed to load pool:', err);
      showMessage('error', 'Failed to load pool details');
    } finally {
      setAdminPoolLoading(false);
    }
  };

  // Load leaderboard data for all pools
  const loadLeaderboardData = async () => {
    setLeaderboardLoading(true);
    try {
      const response = await getAdminLeaderboards();
      setLeaderboardData(response.data);
    } catch (err) {
      console.error('Failed to load leaderboards:', err);
      showMessage('error', 'Failed to load leaderboard data');
    } finally {
      setLeaderboardLoading(false);
    }
  };

  // Load activity logs
  const loadDuplicates = async () => {
    setDuplicatesLoading(true);
    try {
      const res = await adminListDuplicateEvents();
      setDuplicateGroups(res.data?.groups || []);
    } catch (err) {
      showMessage('error', err.response?.data?.error || 'Failed to load duplicate events');
    } finally {
      setDuplicatesLoading(false);
    }
  };

  const handleMergeDuplicates = async (keepEventId, dropEventId, dropName) => {
    if (merging) return;
    if (!window.confirm(
      `Merge "${dropName}" into the kept event?\n\n` +
      `All fights, picks, scores, and badges will move to the kept event, then "${dropName}" will be deleted. This cannot be undone.`
    )) return;
    setMerging(true);
    try {
      const res = await adminMergeDuplicateEvents(keepEventId, dropEventId);
      const moved = res.data?.moved || {};
      const summary = Object.entries(moved)
        .filter(([, n]) => n > 0)
        .map(([k, n]) => `${k}: ${n}`)
        .join(', ');
      showMessage('success', `Merged. ${summary || 'No rows needed moving.'}`);
      await loadDuplicates();
    } catch (err) {
      showMessage('error', err.response?.data?.error || 'Failed to merge events');
    } finally {
      setMerging(false);
    }
  };

  const loadActivityLogs = async (filter = 'all') => {
    setActivityLoading(true);
    try {
      const params = { limit: 200 };
      if (filter !== 'all') {
        params.actionType = filter;
      }
      const response = await getAdminActivityLogs(params);
      setActivityLogs(response.data.logs || []);
      setActivityStats(response.data.stats || null);
    } catch (err) {
      console.error('Failed to load activity logs:', err);
      showMessage('error', 'Failed to load activity logs');
    } finally {
      setActivityLoading(false);
    }
  };

  const loadChatPools = async () => {
    setChatPoolsLoading(true);
    try {
      const response = await getAdminChatPools();
      setChatPools(response.data || []);
    } catch (err) {
      console.error('Failed to load chat pools:', err);
      showMessage('error', 'Failed to load chat pools');
    } finally {
      setChatPoolsLoading(false);
    }
  };

  const openChatPool = async (pool) => {
    setSelectedChatPool(pool);
    setChatMessages([]);
    setChatMessagesLoading(true);
    try {
      const response = await getAdminChatMessages(pool.league_id, { limit: 200 });
      setChatMessages(response.data?.messages || []);
    } catch (err) {
      console.error('Failed to load chat messages:', err);
      showMessage('error', 'Failed to load chat messages');
    } finally {
      setChatMessagesLoading(false);
    }
  };

  // Notifications tab functions
  const loadNotificationLogs = async () => {
    setNotificationLogsLoading(true);
    try {
      const response = await adminGetNotificationLogs();
      setNotificationLogs(response.data || []);
    } catch (err) {
      console.error('Failed to load notification logs:', err);
      showMessage('error', 'Failed to load notification logs');
    } finally {
      setNotificationLogsLoading(false);
    }
  };

  const loadScheduledNotifications = async () => {
    setScheduledLoading(true);
    try {
      const response = await adminGetScheduledNotifications();
      setScheduledNotifications(response.data || []);
    } catch (err) {
      console.error('Failed to load scheduled notifications:', err);
      showMessage('error', 'Failed to load scheduled notifications');
    } finally {
      setScheduledLoading(false);
    }
  };

  const handleSendNotification = async () => {
    if (!notifTitle.trim() || !notifBody.trim()) {
      showMessage('error', 'Title and body are required');
      return;
    }
    if (notifAudienceType === 'pool' && !notifAudiencePoolId) {
      showMessage('error', 'Select a pool');
      return;
    }
    setNotifSending(true);
    try {
      const response = await adminSendNotification({
        title: notifTitle.trim(),
        body: notifBody.trim(),
        audienceType: notifAudienceType,
        audiencePoolId: notifAudienceType === 'pool' ? Number(notifAudiencePoolId) : undefined,
      });
      showMessage('success', `Queued for ${response.data.total} device${response.data.total === 1 ? '' : 's'} (${response.data.sent} dispatch attempted — live delivery is pending APNS/FCM credentials, see CLAUDE.md)`);
      setNotifTitle('');
      setNotifBody('');
      loadNotificationLogs();
    } catch (err) {
      console.error('Failed to send notification:', err);
      showMessage('error', err.response?.data?.error || 'Failed to send notification');
    } finally {
      setNotifSending(false);
    }
  };

  const resetScheduleForm = () => {
    setScheduleForm({
      title: '', body: '', audienceType: 'all', audiencePoolId: '',
      triggerType: 'daily', triggerTime: '09:00', triggerDay: 0, hoursBeforeLock: 2,
    });
    setShowScheduleForm(false);
  };

  const handleCreateScheduledNotification = async () => {
    if (!scheduleForm.title.trim() || !scheduleForm.body.trim()) {
      showMessage('error', 'Title and body are required');
      return;
    }
    if (scheduleForm.audienceType === 'pool' && !scheduleForm.audiencePoolId) {
      showMessage('error', 'Select a pool');
      return;
    }
    setScheduleSaving(true);
    try {
      await adminCreateScheduledNotification({
        title: scheduleForm.title.trim(),
        body: scheduleForm.body.trim(),
        audienceType: scheduleForm.audienceType,
        audiencePoolId: scheduleForm.audienceType === 'pool' ? Number(scheduleForm.audiencePoolId) : undefined,
        triggerType: scheduleForm.triggerType,
        triggerTime: scheduleForm.triggerTime,
        triggerDay: Number(scheduleForm.triggerDay),
        hoursBeforeLock: Number(scheduleForm.hoursBeforeLock),
      });
      showMessage('success', 'Scheduled notification created');
      resetScheduleForm();
      loadScheduledNotifications();
    } catch (err) {
      console.error('Failed to create scheduled notification:', err);
      showMessage('error', err.response?.data?.error || 'Failed to create scheduled notification');
    } finally {
      setScheduleSaving(false);
    }
  };

  const toggleScheduledNotification = async (rule) => {
    try {
      await adminUpdateScheduledNotification(rule.id, { isEnabled: !rule.is_enabled });
      loadScheduledNotifications();
    } catch (err) {
      console.error('Failed to toggle scheduled notification:', err);
      showMessage('error', 'Failed to update scheduled notification');
    }
  };

  const handleDeleteScheduledNotification = async (rule) => {
    if (!window.confirm(`Delete the scheduled notification "${rule.title}"?`)) return;
    try {
      await adminDeleteScheduledNotification(rule.id);
      showMessage('success', 'Scheduled notification deleted');
      loadScheduledNotifications();
    } catch (err) {
      console.error('Failed to delete scheduled notification:', err);
      showMessage('error', 'Failed to delete scheduled notification');
    }
  };

  // 2FA setup functions
  const handleSetup2FA = async () => {
    setTwoFALoading(true);
    try {
      const response = await adminSetup2FA();
      setSetup2FAData(response.data);
      setSetup2FACode('');
    } catch (err) {
      showMessage('error', err.response?.data?.error || 'Failed to set up 2FA');
    } finally {
      setTwoFALoading(false);
    }
  };

  const handleVerify2FASetup = async (e) => {
    e.preventDefault();
    setTwoFALoading(true);
    try {
      await adminVerify2FASetup(setup2FACode);
      showMessage('success', '2FA enabled successfully');
      setTwoFactorEnabled(true);
      setSetup2FAData(null);
      setSetup2FACode('');
    } catch (err) {
      showMessage('error', err.response?.data?.error || 'Invalid verification code');
    } finally {
      setTwoFALoading(false);
    }
  };

  const handleDisable2FA = async (e) => {
    e.preventDefault();
    setTwoFALoading(true);
    try {
      await adminDisable2FA(disable2FAPassword, disable2FACode);
      showMessage('success', '2FA disabled successfully');
      setTwoFactorEnabled(false);
      setDisable2FAPassword('');
      setDisable2FACode('');
    } catch (err) {
      showMessage('error', err.response?.data?.error || 'Failed to disable 2FA');
    } finally {
      setTwoFALoading(false);
    }
  };

  if (loading) return <Loading />;

  // Login form
  if (!isAuthenticated) {
    // 2FA verification step
    if (requires2FA) {
      return (
        <div className="container" style={{ maxWidth: '400px', paddingTop: '80px' }}>
          <div className="card">
            <h2 style={{ textAlign: 'center', marginBottom: '8px' }}>Two-Factor Authentication</h2>
            <p style={{ textAlign: 'center', color: 'var(--text-muted)', marginBottom: '24px' }}>
              Enter the 6-digit code from your authenticator app
            </p>

            {loginError && (
              <div style={{
                background: 'rgba(239, 68, 68, 0.1)',
                border: '1px solid #ef4444',
                borderRadius: '8px',
                padding: '12px',
                marginBottom: '16px',
                color: '#ef4444'
              }}>
                {loginError}
              </div>
            )}

            <form onSubmit={handleVerify2FA}>
              <div className="form-group">
                <label className="form-label">Verification Code</label>
                <input
                  type="text"
                  className="form-input"
                  value={totpCode}
                  onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  style={{ textAlign: 'center', fontSize: '24px', letterSpacing: '8px' }}
                  maxLength="6"
                  autoFocus
                  required
                />
              </div>
              <button
                type="submit"
                className="btn btn-primary"
                style={{ width: '100%' }}
                disabled={totpCode.length !== 6}
              >
                Verify
              </button>
            </form>

            <button
              type="button"
              onClick={handleBack2FA}
              className="btn btn-secondary"
              style={{ width: '100%', marginTop: '12px' }}
            >
              Back to Login
            </button>
          </div>
        </div>
      );
    }

    // Regular login form
    return (
      <div className="container" style={{ maxWidth: '400px', paddingTop: '80px' }}>
        <div className="card">
          <h2 style={{ textAlign: 'center', marginBottom: '24px' }}>Admin Login</h2>

          {loginError && (
            <div style={{
              background: 'rgba(239, 68, 68, 0.1)',
              border: '1px solid #ef4444',
              borderRadius: '8px',
              padding: '12px',
              marginBottom: '16px',
              color: '#ef4444'
            }}>
              {loginError}
            </div>
          )}

          <form onSubmit={handleLogin}>
            <div className="form-group">
              <label className="form-label">Username</label>
              <input
                type="text"
                className="form-input"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
              />
            </div>
            <div className="form-group">
              <label className="form-label">Password</label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showPassword ? 'text' : 'password'}
                  className="form-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  style={{ paddingRight: '40px' }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={{
                    position: 'absolute',
                    right: '8px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '4px',
                    color: 'var(--text-muted)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                  title={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                      <circle cx="12" cy="12" r="3"></circle>
                    </svg>
                  ) : (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                      <line x1="1" y1="1" x2="23" y2="23"></line>
                    </svg>
                  )}
                </button>
              </div>
            </div>
            <button type="submit" className="btn btn-primary" style={{ width: '100%' }}>
              Login
            </button>
          </form>

        </div>
      </div>
    );
  }

  // Admin Dashboard
  const allEvents = dashboard?.allEvents || [];
  const currentEvent = dashboard?.currentEvent?.event;
  // Sort fights by fight_order within each card type — used by the per-card
  // breakdown inside the Cards tab.
  const sortByFightOrder = (a, b) => (a.fight_order ?? 0) - (b.fight_order ?? 0);
  const isLive = selectedEvent?.status === 'locked';

  return (
    <div className="container" style={{ paddingTop: '24px' }}>
      {/* CSS for live indicator animation */}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <h1 style={{ margin: 0 }}>Admin Dashboard</h1>
          {isLive && (
            <span style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              background: 'rgba(239, 68, 68, 0.2)',
              border: '1px solid rgba(239, 68, 68, 0.5)',
              color: '#ef4444',
              padding: '4px 12px',
              borderRadius: '20px',
              fontSize: '12px',
              fontWeight: '600',
              textTransform: 'uppercase',
              animation: 'pulse 2s infinite'
            }}>
              <span style={{
                width: '8px',
                height: '8px',
                background: '#ef4444',
                borderRadius: '50%',
                animation: 'pulse 1.5s infinite'
              }}></span>
              LIVE
            </span>
          )}
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          {isLive && (
            <>
              <button
                onClick={() => refreshEventData(selectedEvent?.id)}
                disabled={isRefreshing}
                className="btn btn-secondary"
                style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
              >
                <span style={{
                  display: 'inline-block',
                  width: '14px',
                  height: '14px',
                  border: '2px solid currentColor',
                  borderTopColor: 'transparent',
                  borderRadius: '50%',
                  animation: isRefreshing ? 'spin 1s linear infinite' : 'none'
                }}></span>
                {isRefreshing ? 'Refreshing...' : 'Refresh'}
              </button>
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px', color: 'var(--text-muted)' }}>
                <input
                  type="checkbox"
                  checked={autoRefreshEnabled}
                  onChange={(e) => setAutoRefreshEnabled(e.target.checked)}
                />
                Auto-refresh
              </label>
            </>
          )}
          <button onClick={handleLogout} className="btn btn-secondary">
            Logout
          </button>
        </div>
      </div>

      {/* Last updated indicator for live events */}
      {isLive && lastUpdated && (
        <div style={{
          fontSize: '12px',
          color: 'var(--text-muted)',
          marginBottom: '16px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <span>Last updated: {lastUpdated.toLocaleTimeString()}</span>
          <span>•</span>
          <span>{fights.filter(f => f.winner != null).length}/{fights.length} fights completed</span>
          {autoRefreshEnabled && <span>• Auto-refreshes every 30s</span>}
        </div>
      )}

      {message.text && (
        <div style={{
          background: message.type === 'error' ? 'rgba(239, 68, 68, 0.1)' : 'rgba(34, 197, 94, 0.1)',
          border: `1px solid ${message.type === 'error' ? '#ef4444' : '#22c55e'}`,
          borderRadius: '8px',
          padding: '12px',
          marginBottom: '16px',
          color: message.type === 'error' ? '#ef4444' : '#22c55e'
        }}>
          {message.text}
        </div>
      )}

      {/* Stats Cards */}
      {selectedEvent && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '16px', marginBottom: '24px' }}>
          <div className="card" style={{ textAlign: 'center' }}>
            <p style={{ color: 'var(--text-muted)', fontSize: '14px', marginBottom: '4px' }}>Total Events</p>
            <p style={{ fontSize: '32px', fontWeight: '700' }}>
              {allEvents.length}
            </p>
          </div>
          <div className="card" style={{ textAlign: 'center' }}>
            <p style={{ color: 'var(--text-muted)', fontSize: '14px', marginBottom: '4px' }}>Current Event</p>
            <p style={{ fontSize: '16px', fontWeight: '600', color: '#22c55e', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
              <span style={{ color: '#22c55e', fontSize: '14px' }}>★</span>
              {currentEvent?.name || 'None'}
            </p>
          </div>
          <div className="card" style={{ textAlign: 'center' }}>
            <p style={{ color: 'var(--text-muted)', fontSize: '14px', marginBottom: '4px' }}>Event Status</p>
            <p style={{ fontSize: '20px', fontWeight: '600', color: selectedEvent.status === 'open' ? '#22c55e' : selectedEvent.status === 'locked' ? '#ef4444' : 'var(--primary)', textTransform: 'capitalize' }}>
              {selectedEvent.status}
            </p>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '24px', borderBottom: '1px solid var(--border)', paddingBottom: '8px', flexWrap: 'wrap' }}>
        {['cards', 'pools', 'leaderboard', 'users', 'activity', 'chat', 'notifications', 'duplicates', 'settings'].map(tab => (
          <button
            key={tab}
            className={`btn ${activeTab === tab ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => {
              setActiveTab(tab);
              if (tab === 'users' && !poolsData) {
                loadPoolsData();
              }
              if (tab === 'pools' && adminPoolsList.length === 0) {
                loadPoolsList();
              }
              if (tab === 'leaderboard' && !leaderboardData) {
                loadLeaderboardData();
              }
              if (tab === 'activity' && activityLogs.length === 0) {
                loadActivityLogs();
              }
              if (tab === 'chat' && chatPools.length === 0) {
                loadChatPools();
              }
              if (tab === 'notifications') {
                if (chatPools.length === 0) loadChatPools();
                loadNotificationLogs();
                loadScheduledNotifications();
              }
              if (tab === 'duplicates') {
                loadDuplicates();
              }
            }}
            style={{ textTransform: 'capitalize' }}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* Cards Tab — unified events view */}
      {activeTab === 'cards' && (() => {
        const sortedEvents = [...allEvents].sort((a, b) => new Date(a.date) - new Date(b.date));
        const now = new Date();
        const isPastDate = (d) => new Date(d) < now;
        // Current = exactly ONE card, chosen with the same priority as
        // GET /api/events/current: a card that started in the last 24h and
        // is still locked (live / awaiting results), else a reopened past
        // card, else an upcoming card with picks closed early, else the
        // soonest upcoming open card, else the soonest upcoming draft. Every other event is Upcoming (date still ahead) or Past.
        const liveSince = new Date(now.getTime() - 24 * 60 * 60 * 1000);
        const newestFirst = [...sortedEvents].reverse();
        const currentEvent =
          newestFirst.find(e => e.status === 'locked' && isPastDate(e.date) && new Date(e.date) >= liveSince) ||
          sortedEvents.find(e => e.status === 'open' && e.manual_override && isPastDate(e.date)) ||
          sortedEvents.find(e => e.status === 'locked' && !isPastDate(e.date)) || // picks closed early
          sortedEvents.find(e => e.status === 'open' && !isPastDate(e.date)) ||
          sortedEvents.find(e => e.status === 'draft' && !isPastDate(e.date));
        const current = currentEvent ? [currentEvent] : [];
        const isCurrent = (e) => e.id === currentEvent?.id;
        const future = sortedEvents.filter(e =>
          !isCurrent(e) && e.status !== 'completed' && !isPastDate(e.date)
        );
        const past = sortedEvents
          .filter(e => !isCurrent(e) && (e.status === 'completed' || isPastDate(e.date)))
          .reverse(); // newest first

        const renderEventCard = (event) => {
          const isActive = selectedEvent?.id === event.id;
          const isCompleted = event.status === 'completed';
          const fightsForCard = isActive ? fights : [];
          const cardMain = fightsForCard.filter(f => f.card_type === 'main').sort(sortByFightOrder);
          const cardPrelims = fightsForCard.filter(f => f.card_type === 'prelim').sort(sortByFightOrder);
          const cardEarly = fightsForCard.filter(f => f.card_type === 'early').sort(sortByFightOrder);
          const cardCompleted = fightsForCard.filter(f => f.winner != null).length;
          const startDate = new Date(event.date);
          const isStarted = startDate < now;

          const statusColor =
            event.status === 'open' ? '#22c55e' :
            event.status === 'locked' ? '#f59e0b' :
            event.status === 'completed' ? 'var(--text-muted)' : 'var(--text-muted)';
          const statusLabel =
            event.status === 'open' ? 'Open' :
            event.status === 'locked' ? 'Locked' :
            event.status === 'completed' ? 'Completed' : 'Draft';

          return (
            <div key={event.id} className="card" style={{ marginBottom: '12px', padding: isActive ? '20px' : '14px 20px' }}>
              {/* Card header — always visible */}
              <div
                onClick={() => {
                  if (isActive) {
                    setSelectedEvent(null);
                    setFights([]);
                  } else {
                    loadEventDetails(event.id);
                  }
                }}
                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', gap: '12px', flexWrap: 'wrap' }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                    <h3 style={{ margin: 0, fontSize: '16px' }}>{event.name}</h3>
                    <span style={{ color: statusColor, fontSize: '12px', fontWeight: 600, padding: '2px 8px', borderRadius: '10px', background: `${statusColor}1f`, border: `1px solid ${statusColor}40` }}>
                      {statusLabel}
                    </span>
                  </div>
                  <div style={{ color: 'var(--text-muted)', fontSize: '12px', marginTop: '4px' }}>
                    {formatDateCST(event.date)}
                    {event.venue && <> · {event.venue}</>}
                  </div>
                </div>
                <span style={{ fontSize: '20px', color: 'var(--text-muted)' }}>
                  {isActive ? '−' : '+'}
                </span>
              </div>

              {/* Expanded body */}
              {isActive && (
                <div style={{ marginTop: '16px', borderTop: '1px solid var(--border)', paddingTop: '16px' }}>
                  {/* Status toggle + edit date */}
                  <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '12px' }}>
                    {!isCompleted && (
                      <button
                        className="btn"
                        onClick={() => handleUpdateStatus(event.id, event.status === 'open' ? 'locked' : 'open')}
                        style={{
                          background: event.status === 'open' ? '#22c55e' : 'var(--bg-input)',
                          color: event.status === 'open' ? 'white' : 'var(--text)',
                          padding: '6px 16px',
                          borderRadius: '20px',
                          border: 'none',
                          cursor: 'pointer',
                          fontSize: '13px',
                        }}
                      >
                        {event.status === 'open' ? 'Close Picks' : 'Open Picks'}
                      </button>
                    )}
                    {editingEventDate ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <input
                          type="datetime-local"
                          className="form-input"
                          value={eventDateInput}
                          onChange={(e) => setEventDateInput(e.target.value)}
                          style={{ padding: '6px 10px', fontSize: '13px' }}
                        />
                        <span style={{ color: 'var(--text-muted)', fontSize: '12px' }}>CST</span>
                        <button className="btn btn-primary" onClick={handleUpdateEventDate} style={{ fontSize: '12px', padding: '6px 12px' }}>Save</button>
                        <button className="btn btn-secondary" onClick={() => setEditingEventDate(false)} style={{ fontSize: '12px', padding: '6px 12px' }}>Cancel</button>
                      </div>
                    ) : (
                      <button
                        className="btn btn-secondary"
                        onClick={() => { setEventDateInput(utcToDatetimeLocal(event.date)); setEditingEventDate(true); }}
                        style={{ fontSize: '12px', padding: '6px 12px' }}
                      >
                        Edit date
                      </button>
                    )}
                    {isStarted && event.status === 'open' && (
                      <span style={{ color: event.manual_override ? '#22c55e' : '#f59e0b', fontSize: '12px' }}>
                        {event.manual_override
                          ? 'Manually reopened — will stay open until you close picks'
                          : 'Event has started — picks will auto-lock'}
                      </span>
                    )}
                  </div>

                  {/* Pre-event tools */}
                  {!isCompleted && (
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '12px' }}>
                      <button className="btn btn-primary" onClick={handleRefreshCard} disabled={refreshingCard || !event.source_url} style={{ fontSize: '12px', padding: '6px 12px' }} title={event.source_url ? `Refresh from: ${event.source_url}` : 'No URL — set UFC URL first'}>
                        {refreshingCard ? 'Refreshing...' : 'Refresh Card'}
                      </button>
                      <button className="btn btn-secondary" onClick={handleRepairPicks} disabled={repairingPicks} style={{ fontSize: '12px', padding: '6px 12px' }} title="Fix orphaned picks after refresh">
                        {repairingPicks ? 'Repairing...' : 'Repair Picks'}
                      </button>
                      <button className="btn btn-secondary" onClick={handleFixEarlyPrelims} disabled={fixingEarlyPrelims} style={{ fontSize: '12px', padding: '6px 12px' }} title="Set bottom 3 prelims as early prelims">
                        {fixingEarlyPrelims ? 'Fixing...' : 'Fix Early Prelims'}
                      </button>
                      <button className="btn btn-secondary" onClick={handleFetchOdds} disabled={fetchingOdds} style={{ fontSize: '12px', padding: '6px 12px' }} title="Fetch betting odds from BestFightOdds.com">
                        {fetchingOdds ? 'Fetching...' : 'Fetch Odds'}
                      </button>
                      <button className="btn btn-secondary" onClick={() => handleDeleteEvent(event.id)} style={{ fontSize: '12px', padding: '6px 12px' }}>
                        Delete Event
                      </button>
                    </div>
                  )}

                  {/* Source URL editor */}
                  {!isCompleted && (
                    <div style={{ padding: '12px', background: 'var(--bg-secondary)', borderRadius: '8px', marginBottom: '16px' }}>
                      <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '8px' }}>UFC Source URL:</div>
                      {event.source_url ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                          <code style={{ fontSize: '11px', background: 'var(--bg-input)', padding: '4px 8px', borderRadius: '4px', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {event.source_url}
                          </code>
                          <button className="btn btn-secondary" onClick={() => setEditingSourceUrl(event.source_url)} style={{ fontSize: '11px', padding: '4px 8px' }}>Edit</button>
                        </div>
                      ) : (
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                          <input type="text" className="form-input" placeholder="https://www.ufc.com/event/ufc-..." value={editingSourceUrl} onChange={(e) => setEditingSourceUrl(e.target.value)} style={{ fontSize: '12px', padding: '6px 8px', flex: 1 }} />
                          <button className="btn btn-primary" onClick={handleSaveSourceUrl} disabled={!editingSourceUrl.trim() || savingSourceUrl} style={{ fontSize: '12px', padding: '6px 12px' }}>
                            {savingSourceUrl ? 'Saving...' : 'Save URL'}
                          </button>
                        </div>
                      )}
                      {editingSourceUrl && event.source_url && (
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: '8px' }}>
                          <input type="text" className="form-input" value={editingSourceUrl} onChange={(e) => setEditingSourceUrl(e.target.value)} style={{ fontSize: '12px', padding: '6px 8px', flex: 1 }} />
                          <button className="btn btn-primary" onClick={handleSaveSourceUrl} disabled={!editingSourceUrl.trim() || savingSourceUrl} style={{ fontSize: '12px', padding: '6px 12px' }}>
                            {savingSourceUrl ? 'Saving...' : 'Save'}
                          </button>
                          <button className="btn btn-secondary" onClick={() => setEditingSourceUrl('')} style={{ fontSize: '12px', padding: '6px 12px' }}>Cancel</button>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Results tools — available for any event past draft so the
                      stats URL can be set before fight night and auto-scraping
                      has something to point at while the event is locked. */}
                  {event.status !== 'draft' && (
                    <div style={{ background: 'var(--bg-input)', borderRadius: '8px', padding: '14px', marginBottom: '16px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', flexWrap: 'wrap' }}>
                        <span style={{ fontWeight: 600, fontSize: '13px' }}>UFC Stats URL</span>
                        <span style={{ fontSize: '11px', padding: '2px 6px', borderRadius: '4px', background: event.ufc_stats_url ? 'rgba(34, 197, 94, 0.2)' : 'rgba(239, 68, 68, 0.2)', color: event.ufc_stats_url ? '#22c55e' : '#ef4444' }}>
                          {event.ufc_stats_url ? 'Configured' : 'Not Set'}
                        </span>
                        <span style={{ marginLeft: 'auto', color: 'var(--text-muted)', fontSize: '12px' }}>
                          {cardCompleted}/{fightsForCard.length} results entered
                        </span>
                      </div>
                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        <input type="text" className="form-input" placeholder="http://ufcstats.com/event-details/..." value={editingUfcStatsUrl} onChange={(e) => setEditingUfcStatsUrl(e.target.value)} style={{ flex: 1, minWidth: '220px', fontSize: '12px' }} />
                        <button className="btn btn-secondary" onClick={handleAutoDetectStatsUrl} disabled={detectingStatsUrl} style={{ whiteSpace: 'nowrap', fontSize: '12px', padding: '6px 12px' }}>
                          {detectingStatsUrl ? 'Detecting...' : 'Auto-Detect'}
                        </button>
                        <button className="btn btn-secondary" onClick={handleSaveUfcStatsUrl} disabled={savingUfcStatsUrl} style={{ whiteSpace: 'nowrap', fontSize: '12px', padding: '6px 12px' }}>
                          {savingUfcStatsUrl ? 'Saving...' : 'Save URL'}
                        </button>
                        {event.ufc_stats_url && (
                          <button className="btn btn-primary" onClick={handleScrapeResults} disabled={scrapingResults} style={{ whiteSpace: 'nowrap', fontSize: '12px', padding: '6px 12px' }}>
                            {scrapingResults ? 'Scraping...' : 'Scrape Now'}
                          </button>
                        )}
                        {/* Delete is shown by the pre-event toolbar for !completed
                            events; only render here when nothing else will. */}
                        {isCompleted && (
                          <button className="btn btn-secondary" onClick={() => handleDeleteEvent(event.id)} style={{ fontSize: '12px', padding: '6px 12px' }}>
                            Delete Event
                          </button>
                        )}
                      </div>
                      {scrapeMessage && (
                        <div style={{ marginTop: '10px', padding: '8px 12px', borderRadius: '6px', fontSize: '13px', background: scrapeMessage.type === 'success' ? 'rgba(34, 197, 94, 0.1)' : 'rgba(239, 68, 68, 0.1)', color: scrapeMessage.type === 'success' ? '#22c55e' : '#ef4444' }}>
                          {scrapeMessage.text}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Fight list */}
                  {fightsForCard.length === 0 ? (
                    <p style={{ color: 'var(--text-muted)', fontSize: '13px', margin: 0 }}>No fights yet on this card.</p>
                  ) : (
                    <>
                      {cardMain.length > 0 && (
                        <>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 0', borderBottom: '1px solid var(--border)', marginBottom: '8px' }}>
                            <h4 style={{ margin: 0, fontSize: '14px' }}>Main Card</h4>
                            <span style={{ color: 'var(--text-muted)', fontSize: '12px' }}>
                              {isCompleted
                                ? `(${cardMain.filter(f => f.winner).length}/${cardMain.length} entered)`
                                : `(${cardMain.length} fights) — 5pts + 3pt method`}
                            </span>
                          </div>
                          {cardMain.map(fight => isCompleted ? (
                            <ExpandableFightResult
                              key={fight.id}
                              fight={fight}
                              expanded={expandedFights[fight.id]}
                              onToggle={() => toggleFight(fight.id)}
                              onUpdateResult={handleUpdateResult}
                              requireMethod={true}
                            />
                          ) : (
                            <FightRowCompact
                              key={fight.id}
                              fight={fight}
                              expanded={expandedFights[fight.id]}
                              onToggle={() => toggleFight(fight.id)}
                              onCancel={handleToggleCancelFight}
                              onDelete={handleDeleteFight}
                              onUpdate={(data) => handleUpdateFightDetails(fight.id, data)}
                            />
                          ))}
                        </>
                      )}
                      {cardPrelims.length > 0 && (
                        <>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 0', borderBottom: '1px solid var(--border)', marginBottom: '8px', marginTop: '20px' }}>
                            <h4 style={{ margin: 0, fontSize: '14px' }}>Prelims</h4>
                            <span style={{ color: 'var(--text-muted)', fontSize: '12px' }}>
                              {isCompleted
                                ? `(${cardPrelims.filter(f => f.winner).length}/${cardPrelims.length} entered)`
                                : `(${cardPrelims.length} fights) — 3pts each`}
                            </span>
                          </div>
                          {cardPrelims.map(fight => isCompleted ? (
                            <ExpandableFightResult
                              key={fight.id}
                              fight={fight}
                              expanded={expandedFights[fight.id]}
                              onToggle={() => toggleFight(fight.id)}
                              onUpdateResult={handleUpdateResult}
                              requireMethod={true}
                            />
                          ) : (
                            <FightRowCompact
                              key={fight.id}
                              fight={fight}
                              expanded={expandedFights[fight.id]}
                              onToggle={() => toggleFight(fight.id)}
                              onCancel={handleToggleCancelFight}
                              onDelete={handleDeleteFight}
                              onUpdate={(data) => handleUpdateFightDetails(fight.id, data)}
                            />
                          ))}
                        </>
                      )}
                      {cardEarly.length > 0 && (
                        <>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 0', borderBottom: '1px solid var(--border)', marginBottom: '8px', marginTop: '20px' }}>
                            <h4 style={{ margin: 0, fontSize: '14px' }}>Early Prelims</h4>
                            <span style={{ color: 'var(--text-muted)', fontSize: '12px' }}>
                              {isCompleted
                                ? `(${cardEarly.filter(f => f.winner).length}/${cardEarly.length} entered)`
                                : `(${cardEarly.length} fights) — 1pt each`}
                            </span>
                          </div>
                          {cardEarly.map(fight => isCompleted ? (
                            <ExpandableFightResult
                              key={fight.id}
                              fight={fight}
                              expanded={expandedFights[fight.id]}
                              onToggle={() => toggleFight(fight.id)}
                              onUpdateResult={handleUpdateResult}
                              requireMethod={true}
                            />
                          ) : (
                            <FightRowCompact
                              key={fight.id}
                              fight={fight}
                              expanded={expandedFights[fight.id]}
                              onToggle={() => toggleFight(fight.id)}
                              onCancel={handleToggleCancelFight}
                              onDelete={handleDeleteFight}
                              onUpdate={(data) => handleUpdateFightDetails(fight.id, data)}
                            />
                          ))}
                        </>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          );
        };

        const sectionHeader = (label) => (
          <div style={{
            display: 'flex', alignItems: 'center', gap: '12px',
            margin: '24px 0 12px',
            color: 'var(--text-muted)',
            fontSize: '12px', fontWeight: 700, letterSpacing: '2px', textTransform: 'uppercase',
          }}>
            <span>{label}</span>
            <div style={{ flex: 1, height: '1px', background: 'var(--border)' }} />
          </div>
        );

        return (
          <div>
            {/* Top toolbar — import upcoming + create manually */}
            <div className="card" style={{ marginBottom: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                <div>
                  <h3 style={{ margin: 0, fontSize: '15px' }}>Add an event</h3>
                  <p style={{ margin: '2px 0 0', color: 'var(--text-muted)', fontSize: '12px' }}>
                    Pull this week's UFC card or create one manually.
                  </p>
                </div>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  <button type="button" className="btn btn-primary" onClick={handleImportAllUpcoming} disabled={importing}>
                    {importing ? 'Importing…' : 'Import Upcoming'}
                  </button>
                  <button type="button" className="btn btn-secondary" onClick={() => setShowCreateEventForm(v => !v)}>
                    {showCreateEventForm ? 'Cancel' : 'Create Manually'}
                  </button>
                </div>
              </div>
              {showCreateEventForm && (
                <form onSubmit={(e) => { handleCreateEvent(e); setShowCreateEventForm(false); }} style={{ marginTop: '14px', borderTop: '1px solid var(--border)', paddingTop: '14px' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px' }}>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label className="form-label">Event Name</label>
                      <input type="text" className="form-input" value={newEvent.name} onChange={(e) => setNewEvent({ ...newEvent, name: e.target.value })} placeholder="UFC 324" required />
                    </div>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label className="form-label">Date & Time</label>
                      <input type="datetime-local" className="form-input" value={newEvent.date} onChange={(e) => setNewEvent({ ...newEvent, date: e.target.value })} required />
                    </div>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label className="form-label">Venue</label>
                      <input type="text" className="form-input" value={newEvent.venue} onChange={(e) => setNewEvent({ ...newEvent, venue: e.target.value })} placeholder="T-Mobile Arena, Las Vegas" required />
                    </div>
                  </div>
                  <button type="submit" className="btn btn-primary" style={{ marginTop: '12px' }}>Create Event</button>
                </form>
              )}
            </div>

            {allEvents.length === 0 ? (
              <div className="card" style={{ textAlign: 'center' }}>
                <p style={{ color: 'var(--text-muted)' }}>No events yet. Use the buttons above to import or create one.</p>
              </div>
            ) : (
              <>
                {current.length > 0 && (
                  <>
                    {sectionHeader('Current')}
                    {current.map(renderEventCard)}
                  </>
                )}
                {future.length > 0 && (
                  <>
                    {sectionHeader('Upcoming')}
                    {future.map(renderEventCard)}
                  </>
                )}
                {past.length > 0 && (
                  <>
                    {sectionHeader('Past')}
                    {past.map(renderEventCard)}
                  </>
                )}
              </>
            )}
          </div>
        );
      })()}




      {/* Pools Tab */}
      {activeTab === 'pools' && (
        <div>
          {adminPoolLoading && !selectedAdminPool ? (
            <div className="card" style={{ textAlign: 'center', padding: '48px' }}>
              <p>Loading pools...</p>
            </div>
          ) : selectedAdminPool ? (
            // Pool Detail View
            <div>
              <button
                className="btn btn-secondary"
                onClick={() => setSelectedAdminPool(null)}
                style={{ marginBottom: '16px' }}
              >
                &larr; Back to Pools
              </button>

              <div className="card" style={{ marginBottom: '24px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '16px' }}>
                  <div>
                    <h2 style={{ marginBottom: '8px' }}>{selectedAdminPool.name}</h2>
                    {selectedAdminPool.description && (
                      <p style={{ color: 'var(--text-muted)', marginBottom: '8px' }}>{selectedAdminPool.description}</p>
                    )}
                    <p style={{ color: 'var(--text-muted)', fontSize: '14px' }}>
                      Invite Code: <strong style={{ color: 'var(--primary)', letterSpacing: '2px' }}>{selectedAdminPool.invite_code}</strong>
                    </p>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <p style={{ color: 'var(--text-muted)', fontSize: '14px' }}>Commissioner</p>
                    <p style={{ fontWeight: '600' }}>{selectedAdminPool.commissioner?.username || 'Unknown'}</p>
                    <p style={{ color: 'var(--text-muted)', fontSize: '12px' }}>{selectedAdminPool.commissioner?.email}</p>
                    {selectedAdminPool.commissioner?.venmo_handle && (
                      <p style={{ color: 'var(--primary)', fontSize: '12px' }}>@{selectedAdminPool.commissioner.venmo_handle}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Pool Stats */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '16px', marginBottom: '24px' }}>
                <div className="card" style={{ textAlign: 'center' }}>
                  <p style={{ color: 'var(--text-muted)', fontSize: '14px', marginBottom: '4px' }}>Members</p>
                  <p style={{ fontSize: '32px', fontWeight: '700' }}>{selectedAdminPool.member_count}</p>
                </div>
                <div className="card" style={{ textAlign: 'center' }}>
                  <p style={{ color: 'var(--text-muted)', fontSize: '14px', marginBottom: '4px' }}>Entry Fee</p>
                  <p style={{ fontSize: '32px', fontWeight: '700' }}>${selectedAdminPool.entry_fee}</p>
                </div>
                <div className="card" style={{ textAlign: 'center' }}>
                  <p style={{ color: 'var(--text-muted)', fontSize: '14px', marginBottom: '4px' }}>Paid</p>
                  <p style={{ fontSize: '32px', fontWeight: '700' }}>{selectedAdminPool.paid_count}/{selectedAdminPool.member_count}</p>
                </div>
                <div className="card" style={{ textAlign: 'center' }}>
                  <p style={{ color: 'var(--text-muted)', fontSize: '14px', marginBottom: '4px' }}>Current Pot</p>
                  <p style={{ fontSize: '32px', fontWeight: '700', color: 'var(--success)' }}>${selectedAdminPool.total_pot}</p>
                </div>
              </div>

              {/* Payout Structure */}
              <div className="card" style={{ marginBottom: '24px' }}>
                <h3 style={{ marginBottom: '12px' }}>Payout Structure</h3>
                <p style={{ color: 'var(--text-muted)' }}>
                  1st: {selectedAdminPool.payout_first}% | 2nd: {selectedAdminPool.payout_second}% | 3rd: {selectedAdminPool.payout_third}%
                </p>
              </div>

              {/* All-Time Leaders - Top 3 */}
              <div style={{ marginBottom: '24px' }}>
                <h3 style={{ marginBottom: '16px' }}>All-Time Leaders</h3>
                {poolAllTimeLeaders.length > 0 ? (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '12px' }}>
                    {poolAllTimeLeaders.map((leader, index) => {
                      const rankColors = ['#fbbf24', '#94a3b8', '#cd7f32'];
                      const rankEmojis = ['👑', '🥈', '🥉'];

                      return (
                        <div
                          key={leader.user_id}
                          className="card"
                          style={{
                            padding: '16px',
                            border: `1px solid ${rankColors[index]}33`
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                            <div style={{
                              width: '36px',
                              height: '36px',
                              borderRadius: '50%',
                              border: `2px solid ${rankColors[index]}`,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0
                            }}>
                              <span style={{ fontSize: '16px' }}>{rankEmojis[index]}</span>
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontWeight: '600', fontSize: '15px' }}>
                                <UserHoverCard userId={leader.user_id} username={leader.username}>
                                  <span style={{ cursor: 'pointer', color: 'var(--primary)' }}>{leader.username}</span>
                                </UserHoverCard>
                              </div>
                              <div style={{ fontSize: '20px', fontWeight: '700', color: 'var(--primary)' }}>
                                {leader.total_points} <span style={{ fontSize: '12px', fontWeight: '400', color: 'var(--text-muted)' }}>pts</span>
                              </div>
                            </div>
                          </div>
                          <div style={{ fontSize: '12px', color: 'var(--text-muted)', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                            <span>{leader.correct_picks || 0}/{leader.total_picks || 0} picks · {leader.pick_accuracy || 0}%</span>
                            {leader.total_method_picks > 0 && (
                              <span>{leader.correct_methods || 0}/{leader.total_method_picks || 0} methods · {leader.method_accuracy || 0}%</span>
                            )}
                            {leader.wins > 0 && (
                              <span style={{ color: '#fbbf24' }}>{leader.wins} win{leader.wins !== 1 ? 's' : ''}</span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p style={{ color: 'var(--text-muted)', fontSize: '14px' }}>
                    No completed events with scored picks yet.
                  </p>
                )}
              </div>

              {/* Current Event Pick Submission Status */}
              {selectedAdminPool.current_event && (
                <div className="card" style={{ marginBottom: '24px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                    <div>
                      <h3 style={{ marginBottom: '4px' }}>Pick Submission Status</h3>
                      <p style={{ color: 'var(--text-muted)', fontSize: '14px' }}>
                        {selectedAdminPool.current_event.name} • {selectedAdminPool.current_event.total_fights} fights
                        <span style={{
                          marginLeft: '8px',
                          padding: '2px 8px',
                          borderRadius: '4px',
                          fontSize: '12px',
                          textTransform: 'uppercase',
                          fontWeight: '600',
                          background: selectedAdminPool.current_event.status === 'open' ? 'rgba(34, 197, 94, 0.2)' :
                                     selectedAdminPool.current_event.status === 'locked' ? 'rgba(239, 68, 68, 0.2)' : 'rgba(var(--primary-rgb), 0.2)',
                          color: selectedAdminPool.current_event.status === 'open' ? '#22c55e' :
                                selectedAdminPool.current_event.status === 'locked' ? '#ef4444' : 'var(--primary)'
                        }}>
                          {selectedAdminPool.current_event.status}
                        </span>
                      </p>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <p style={{ fontSize: '24px', fontWeight: '700', color: 'var(--primary)' }}>
                        {selectedAdminPool.current_event.submitted_count}/{selectedAdminPool.member_count}
                      </p>
                      <p style={{ color: 'var(--text-muted)', fontSize: '12px' }}>Submitted</p>
                    </div>
                  </div>

                  {/* Mini submission list */}
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {(selectedAdminPool.members || []).map(member => {
                      const isComplete = member.picks_complete;
                      const hasPartial = member.picks_submitted > 0 && !isComplete;
                      return (
                        <div
                          key={member.user_id}
                          style={{
                            padding: '6px 12px',
                            borderRadius: '6px',
                            fontSize: '13px',
                            background: isComplete ? 'rgba(34, 197, 94, 0.15)' : hasPartial ? 'rgba(251, 191, 36, 0.15)' : 'rgba(239, 68, 68, 0.15)',
                            color: isComplete ? '#22c55e' : hasPartial ? '#fbbf24' : '#ef4444',
                            border: `1px solid ${isComplete ? 'rgba(34, 197, 94, 0.3)' : hasPartial ? 'rgba(251, 191, 36, 0.3)' : 'rgba(239, 68, 68, 0.3)'}`
                          }}
                        >
                          {member.username} {isComplete ? '✓' : `(${member.picks_submitted}/${member.picks_total})`}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Members Table */}
              <div className="card">
                <h3 style={{ marginBottom: '16px' }}>Members ({selectedAdminPool.members?.length || 0})</h3>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '700px' }}>
                    <thead>
                      <tr style={{ borderBottom: '2px solid var(--border)' }}>
                        <th style={{ textAlign: 'left', padding: '12px 8px', color: 'var(--text-muted)' }}>Username</th>
                        <th style={{ textAlign: 'left', padding: '12px 8px', color: 'var(--text-muted)' }}>Email</th>
                        <th style={{ textAlign: 'left', padding: '12px 8px', color: 'var(--text-muted)' }}>Venmo</th>
                        <th style={{ textAlign: 'center', padding: '12px 8px', color: 'var(--text-muted)' }}>Picks</th>
                        <th style={{ textAlign: 'center', padding: '12px 8px', color: 'var(--text-muted)' }}>Cards Run</th>
                        <th style={{ textAlign: 'center', padding: '12px 8px', color: 'var(--text-muted)' }}>Paid</th>
                        <th style={{ textAlign: 'left', padding: '12px 8px', color: 'var(--text-muted)' }}>Joined</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(selectedAdminPool.members || []).map(member => (
                        <tr key={member.user_id} style={{ borderBottom: '1px solid var(--border)' }}>
                          <td style={{ padding: '12px 8px' }}>
                            <UserHoverCard userId={member.user_id} username={member.username}>
                              <span style={{ fontWeight: member.user_id === selectedAdminPool.commissioner_id ? '600' : '400', cursor: 'pointer', color: 'var(--primary)' }}>
                                {member.username}
                              </span>
                            </UserHoverCard>
                            {member.user_id === selectedAdminPool.commissioner_id && (
                              <span style={{ color: 'var(--primary)', fontSize: '11px', marginLeft: '6px' }}>COMMISH</span>
                            )}
                          </td>
                          <td style={{ padding: '12px 8px', color: 'var(--text-muted)', fontSize: '13px' }}>{member.email}</td>
                          <td style={{ padding: '12px 8px', color: 'var(--primary)', fontSize: '13px' }}>
                            {member.venmo_handle ? `@${member.venmo_handle}` : '-'}
                          </td>
                          <td style={{ padding: '12px 8px', textAlign: 'center' }}>
                            {member.picks_total > 0 ? (
                              <span style={{
                                color: member.picks_complete ? '#22c55e' : member.picks_submitted > 0 ? '#fbbf24' : '#ef4444',
                                fontWeight: '500'
                              }}>
                                {member.picks_submitted}/{member.picks_total}
                                {member.picks_complete && ' ✓'}
                              </span>
                            ) : (
                              <span style={{ color: 'var(--text-muted)' }}>-</span>
                            )}
                          </td>
                          <td style={{ padding: '12px 8px', textAlign: 'center' }}>{member.cards_run}</td>
                          <td style={{ padding: '12px 8px', textAlign: 'center' }}>
                            <span style={{ color: member.has_paid ? 'var(--success)' : 'var(--warning)' }}>
                              {member.has_paid ? 'Yes' : 'No'}
                            </span>
                          </td>
                          <td style={{ padding: '12px 8px', color: 'var(--text-muted)', fontSize: '13px' }}>
                            {member.joined_at ? new Date(member.joined_at).toLocaleDateString() : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          ) : (
            // Pools List View
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' }}>
                <h2>All Pools ({adminPoolsList.length})</h2>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <select
                    value={poolsActiveEvent}
                    onChange={(e) => handlePoolsEventChange(e.target.value)}
                    style={{
                      background: 'var(--bg-card)',
                      color: 'var(--text)',
                      border: '1px solid var(--border)',
                      borderRadius: '8px',
                      padding: '8px 12px',
                      fontSize: '13px',
                      maxWidth: '260px'
                    }}
                  >
                    {poolsEventList.map(event => (
                      <option key={event.id} value={event.id}>
                        {event.name} {event.status === 'locked' ? '(LIVE)' : event.status === 'open' ? '(Open)' : event.status === 'completed' ? '' : '(Draft)'}
                      </option>
                    ))}
                  </select>
                  {selectedPoolIds.size > 0 && (
                    <button
                      className="btn btn-secondary"
                      onClick={copySelectedEmails}
                      style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
                    >
                      {emailsCopied ? 'Copied!' : `Copy ${getSelectedCommissionerEmails().length} Email${getSelectedCommissionerEmails().length !== 1 ? 's' : ''}`}
                    </button>
                  )}
                  <button className="btn btn-secondary" onClick={() => loadPoolsList()}>Refresh</button>
                </div>
              </div>
              <div className="card">
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '500px' }}>
                    <thead>
                      <tr style={{ borderBottom: '2px solid var(--border)' }}>
                        <th style={{ textAlign: 'center', padding: '12px 8px', width: '40px' }}>
                          <input
                            type="checkbox"
                            checked={selectedPoolIds.size === adminPoolsList.length && adminPoolsList.length > 0}
                            onChange={toggleAllPoolsSelection}
                            style={{ cursor: 'pointer' }}
                          />
                        </th>
                        <th
                          style={{ textAlign: 'center', padding: '12px 8px', color: 'var(--text-muted)', cursor: 'pointer', userSelect: 'none', width: '60px' }}
                          onClick={() => handlePoolsSort('id')}
                        >
                          ID <span style={{ opacity: poolsSortField === 'id' ? 1 : 0.3 }}>{poolsSortField === 'id' && poolsSortDirection === 'asc' ? '↑' : '↓'}</span>
                        </th>
                        <th
                          style={{ textAlign: 'left', padding: '12px 8px', color: 'var(--text-muted)', cursor: 'pointer', userSelect: 'none' }}
                          onClick={() => handlePoolsSort('name')}
                        >
                          Pool Name <span style={{ opacity: poolsSortField === 'name' ? 1 : 0.3 }}>{poolsSortField === 'name' && poolsSortDirection === 'asc' ? '↑' : '↓'}</span>
                        </th>
                        <th
                          style={{ textAlign: 'left', padding: '12px 8px', color: 'var(--text-muted)', cursor: 'pointer', userSelect: 'none' }}
                          onClick={() => handlePoolsSort('commissioner_username')}
                        >
                          Commissioner <span style={{ opacity: poolsSortField === 'commissioner_username' ? 1 : 0.3 }}>{poolsSortField === 'commissioner_username' && poolsSortDirection === 'asc' ? '↑' : '↓'}</span>
                        </th>
                        <th
                          style={{ textAlign: 'center', padding: '12px 8px', color: 'var(--text-muted)', cursor: 'pointer', userSelect: 'none' }}
                          onClick={() => handlePoolsSort('member_count')}
                        >
                          Members <span style={{ opacity: poolsSortField === 'member_count' ? 1 : 0.3 }}>{poolsSortField === 'member_count' && poolsSortDirection === 'asc' ? '↑' : '↓'}</span>
                        </th>
                        <th
                          style={{ textAlign: 'center', padding: '12px 8px', color: 'var(--text-muted)', cursor: 'pointer', userSelect: 'none' }}
                          onClick={() => handlePoolsSort('active_users')}
                        >
                          Active <span style={{ opacity: poolsSortField === 'active_users' ? 1 : 0.3 }}>{poolsSortField === 'active_users' && poolsSortDirection === 'asc' ? '↑' : '↓'}</span>
                        </th>
                        <th
                          style={{ textAlign: 'center', padding: '12px 8px', color: 'var(--text-muted)', cursor: 'pointer', userSelect: 'none' }}
                          onClick={() => handlePoolsSort('entry_fee')}
                        >
                          Entry Fee <span style={{ opacity: poolsSortField === 'entry_fee' ? 1 : 0.3 }}>{poolsSortField === 'entry_fee' && poolsSortDirection === 'asc' ? '↑' : '↓'}</span>
                        </th>
                        <th
                          style={{ textAlign: 'left', padding: '12px 8px', color: 'var(--text-muted)', cursor: 'pointer', userSelect: 'none' }}
                          onClick={() => handlePoolsSort('created_at')}
                        >
                          Created <span style={{ opacity: poolsSortField === 'created_at' ? 1 : 0.3 }}>{poolsSortField === 'created_at' && poolsSortDirection === 'asc' ? '↑' : '↓'}</span>
                        </th>
                        <th style={{ textAlign: 'center', padding: '12px 8px', color: 'var(--text-muted)' }}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {getSortedPoolsList().map(pool => (
                        <tr key={pool.id} style={{ borderBottom: '1px solid var(--border)', background: selectedPoolIds.has(pool.id) ? 'rgba(227, 55, 47, 0.1)' : 'transparent' }}>
                          <td style={{ padding: '12px 8px', textAlign: 'center' }}>
                            <input
                              type="checkbox"
                              checked={selectedPoolIds.has(pool.id)}
                              onChange={() => togglePoolSelection(pool.id)}
                              style={{ cursor: 'pointer' }}
                            />
                          </td>
                          <td style={{ padding: '12px 8px', textAlign: 'center', color: 'var(--text-muted)', fontSize: '13px' }}>{pool.id}</td>
                          <td style={{ padding: '12px 8px', fontWeight: '500' }}>{pool.name}</td>
                          <td style={{ padding: '12px 8px', color: 'var(--text-muted)' }}>
                            <UserHoverCard userId={pool.commissioner_id} username={pool.commissioner_username}>
                              <span style={{ cursor: 'pointer', color: 'var(--primary)' }}>{pool.commissioner_username}</span>
                            </UserHoverCard>
                          </td>
                          <td style={{ padding: '12px 8px', textAlign: 'center' }}>{pool.member_count}</td>
                          <td style={{ padding: '12px 8px', textAlign: 'center', color: pool.active_users > 0 ? 'var(--success)' : 'var(--text-muted)' }}>{pool.active_users || 0}</td>
                          <td style={{ padding: '12px 8px', textAlign: 'center' }}>${pool.entry_fee}</td>
                          <td style={{ padding: '12px 8px', color: 'var(--text-muted)', fontSize: '13px' }}>
                            {pool.created_at ? new Date(pool.created_at).toLocaleDateString() : '-'}
                          </td>
                          <td style={{ padding: '12px 8px', textAlign: 'center' }}>
                            <button
                              className="btn btn-primary"
                              style={{ padding: '6px 12px', fontSize: '13px' }}
                              onClick={() => loadPoolDetail(pool.id)}
                            >
                              View
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {adminPoolsList.length === 0 && (
                  <p style={{ textAlign: 'center', color: 'var(--text-muted)', padding: '24px' }}>No pools found</p>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Leaderboard Tab */}
      {activeTab === 'leaderboard' && (
        <div>
          {leaderboardLoading ? (
            <div className="card" style={{ textAlign: 'center', padding: '48px' }}>
              <p>Loading leaderboard data...</p>
            </div>
          ) : leaderboardData ? (
            <div>
              {/* Event Header */}
              {leaderboardData.event && (
                <div className="card" style={{ marginBottom: '24px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
                    <div>
                      <h2 style={{ marginBottom: '4px' }}>{leaderboardData.event.name}</h2>
                      <p style={{ color: 'var(--text-muted)', fontSize: '14px' }}>
                        {new Date(leaderboardData.event.date).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                      </p>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <span style={{
                        padding: '4px 12px',
                        borderRadius: '6px',
                        fontSize: '13px',
                        fontWeight: '600',
                        textTransform: 'uppercase',
                        background: leaderboardData.event.status === 'open' ? 'rgba(34, 197, 94, 0.2)' :
                                   leaderboardData.event.status === 'locked' ? 'rgba(239, 68, 68, 0.2)' :
                                   leaderboardData.event.status === 'completed' ? 'rgba(var(--primary-rgb), 0.2)' : 'rgba(var(--text-muted-rgb), 0.2)',
                        color: leaderboardData.event.status === 'open' ? '#22c55e' :
                              leaderboardData.event.status === 'locked' ? '#ef4444' :
                              leaderboardData.event.status === 'completed' ? 'var(--primary)' : 'var(--text-muted)'
                      }}>
                        {leaderboardData.event.status}
                      </span>
                      <button className="btn btn-secondary" onClick={loadLeaderboardData} style={{ fontSize: '13px', padding: '6px 12px' }}>
                        Refresh
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Pool Leaderboards */}
              {leaderboardData.pools?.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                  {leaderboardData.pools.map(pool => (
                    <div key={pool.id} className="card">
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                        <div>
                          <h3 style={{ marginBottom: '4px' }}>{pool.name}</h3>
                          <p style={{ color: 'var(--text-muted)', fontSize: '13px' }}>
                            {pool.member_count} members • ${pool.entry_fee} entry • {pool.fights?.completed || 0}/{pool.fights?.total || 0} fights completed
                          </p>
                        </div>
                      </div>

                      {pool.standings?.length > 0 ? (
                        <div style={{ overflowX: 'auto' }}>
                          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '500px' }}>
                            <thead>
                              <tr style={{ borderBottom: '2px solid var(--border)' }}>
                                <th style={{ textAlign: 'center', padding: '10px 8px', color: 'var(--text-muted)', width: '60px' }}>Rank</th>
                                <th style={{ textAlign: 'left', padding: '10px 8px', color: 'var(--text-muted)' }}>Player</th>
                                <th style={{ textAlign: 'center', padding: '10px 8px', color: 'var(--text-muted)' }}>Correct</th>
                                <th style={{ textAlign: 'center', padding: '10px 8px', color: 'var(--text-muted)' }}>Method</th>
                                <th style={{ textAlign: 'center', padding: '10px 8px', color: 'var(--text-muted)' }}>Points</th>
                              </tr>
                            </thead>
                            <tbody>
                              {pool.standings.map((entry, idx) => {
                                const rank = entry.rank || idx + 1;
                                const getRankColor = (r) => {
                                  if (r === 1) return '#fbbf24';
                                  if (r === 2) return '#94a3b8';
                                  if (r === 3) return '#cd7f32';
                                  return 'var(--text-muted)';
                                };
                                const rankColor = getRankColor(rank);
                                return (
                                  <tr key={entry.user_id} style={{ borderBottom: '1px solid var(--border)' }}>
                                    <td style={{ textAlign: 'center', padding: '10px 8px' }}>
                                      {rank <= 3 ? (
                                        <div style={{
                                          width: '28px',
                                          height: '28px',
                                          borderRadius: '50%',
                                          border: `2px solid ${rankColor}`,
                                          display: 'inline-flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          fontWeight: '600',
                                          fontSize: '13px',
                                          color: rankColor
                                        }}>
                                          {rank}
                                        </div>
                                      ) : (
                                        <span style={{ color: 'var(--text-muted)', fontWeight: '500' }}>{rank}</span>
                                      )}
                                    </td>
                                    <td style={{ padding: '10px 8px' }}>
                                      <UserHoverCard userId={entry.user_id} username={entry.username}>
                                        <span style={{ fontWeight: '500', cursor: 'pointer', color: 'var(--primary)' }}>
                                          {entry.username}
                                          {entry.display_name && entry.display_name !== entry.username && (
                                            <span style={{ color: 'var(--text-muted)', fontWeight: 400, marginLeft: '6px', fontSize: '12px' }}>
                                              ({entry.display_name})
                                            </span>
                                          )}
                                        </span>
                                      </UserHoverCard>
                                    </td>
                                    <td style={{ textAlign: 'center', padding: '10px 8px', color: 'var(--text-muted)' }}>
                                      {entry.correct_picks || 0}/{pool.fights?.completed || 0}
                                    </td>
                                    <td style={{ textAlign: 'center', padding: '10px 8px' }}>
                                      <span style={{ color: entry.method_bonus > 0 ? '#22c55e' : 'var(--text-muted)', fontWeight: entry.method_bonus > 0 ? '600' : '400' }}>
                                        +{entry.method_bonus || 0}
                                      </span>
                                    </td>
                                    <td style={{ textAlign: 'center', padding: '10px 8px' }}>
                                      <span style={{ fontWeight: '700', fontSize: '16px', color: 'var(--primary)' }}>
                                        {entry.total_points || 0}
                                      </span>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <p style={{ color: 'var(--text-muted)', textAlign: 'center', padding: '24px' }}>No standings available yet</p>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="card" style={{ textAlign: 'center', padding: '48px' }}>
                  <p style={{ color: 'var(--text-muted)' }}>No pools found</p>
                </div>
              )}
            </div>
          ) : (
            <div className="card" style={{ textAlign: 'center', padding: '48px' }}>
              <p style={{ color: 'var(--text-muted)', marginBottom: '16px' }}>No leaderboard data available</p>
              <button className="btn btn-primary" onClick={loadLeaderboardData}>Load Leaderboards</button>
            </div>
          )}
        </div>
      )}

      {/* Data Explorer Tab */}
      {activeTab === 'users' && (
        <div>
          {poolsDataLoading ? (
            <div className="card" style={{ textAlign: 'center', padding: '48px' }}>
              <p>Loading pools data...</p>
            </div>
          ) : poolsData ? (
            <>
              {/* Stats Summary */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '16px', marginBottom: '24px' }}>
                <div className="card" style={{ textAlign: 'center' }}>
                  <p style={{ color: 'var(--text-muted)', fontSize: '14px', marginBottom: '4px' }}>Total Users</p>
                  <p style={{ fontSize: '32px', fontWeight: '700' }}>{poolsData.stats.totalRegisteredUsers}</p>
                </div>
                <div className="card" style={{ textAlign: 'center' }}>
                  <p style={{ color: 'var(--text-muted)', fontSize: '14px', marginBottom: '4px' }}>Total Pools</p>
                  <p style={{ fontSize: '32px', fontWeight: '700' }}>{poolsData.stats.totalPools}</p>
                </div>
                <div className="card" style={{ textAlign: 'center' }}>
                  <p style={{ color: 'var(--text-muted)', fontSize: '14px', marginBottom: '4px' }}>Avg Pools/User</p>
                  <p style={{ fontSize: '32px', fontWeight: '700' }}>
                    {poolsData.stats.totalRegisteredUsers > 0
                      ? (poolsData.stats.totalMemberships / poolsData.stats.totalRegisteredUsers).toFixed(1)
                      : '0'}
                  </p>
                </div>
                <div className="card" style={{ textAlign: 'center' }}>
                  <p style={{ color: 'var(--text-muted)', fontSize: '14px', marginBottom: '4px' }}>Avg Entry Fee</p>
                  <p style={{ fontSize: '32px', fontWeight: '700' }}>
                    ${poolsData.pools.length > 0
                      ? Math.round(poolsData.pools.reduce((sum, p) => sum + (p.entry_fee || 0), 0) / poolsData.pools.length)
                      : '0'}
                  </p>
                </div>
              </div>

              {/* Filters */}
              <div className="card" style={{ marginBottom: '24px' }}>
                <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                  <div style={{ minWidth: '180px' }}>
                    <label className="form-label" style={{ marginBottom: '4px' }}>Pool</label>
                    <select
                      className="form-input"
                      value={selectedPoolFilter}
                      onChange={(e) => setSelectedPoolFilter(e.target.value)}
                    >
                      <option value="all-users">All Users ({poolsData.stats.totalRegisteredUsers})</option>
                      <option value="no-pool">Not In Any Pool ({poolsData.stats.usersNotInPools})</option>
                      <optgroup label="By Pool">
                        {poolsData.pools.map(pool => (
                          <option key={pool.id} value={pool.id}>{pool.name} ({pool.member_count})</option>
                        ))}
                      </optgroup>
                    </select>
                  </div>
                  <div style={{ minWidth: '140px' }}>
                    <label className="form-label" style={{ marginBottom: '4px' }}>Pool Count</label>
                    <select
                      className="form-input"
                      value={poolCountFilter}
                      onChange={(e) => setPoolCountFilter(e.target.value)}
                    >
                      <option value="any">Any</option>
                      <option value="0">0 pools</option>
                      <option value="1">1 pool</option>
                      <option value="2">2 pools</option>
                      <option value="3+">3+ pools</option>
                    </select>
                  </div>
                  <div style={{ minWidth: '140px' }}>
                    <label className="form-label" style={{ marginBottom: '4px' }}>Created</label>
                    <select
                      className="form-input"
                      value={joinedDateFilter}
                      onChange={(e) => setJoinedDateFilter(e.target.value)}
                    >
                      <option value="all-time">All Time</option>
                      <option value="hour">Last Hour</option>
                      <option value="6hours">Last 6 Hours</option>
                      <option value="today">Today</option>
                      <option value="week">Last 7 Days</option>
                      <option value="month">Last 30 Days</option>
                      <option value="90days">Last 90 Days</option>
                    </select>
                  </div>
                  <div style={{ flex: '1', minWidth: '180px' }}>
                    <label className="form-label" style={{ marginBottom: '4px' }}>Search</label>
                    <input
                      type="text"
                      className="form-input"
                      placeholder="Username, email, pool..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                  <button
                    className="btn btn-secondary"
                    onClick={loadPoolsData}
                  >
                    Refresh
                  </button>
                </div>
              </div>

              {/* Data Table - User-centric view */}
              <div className="card" style={{ maxHeight: '600px', overflow: 'auto' }}>
                <div style={{ minWidth: '700px' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ borderBottom: '2px solid var(--border)' }}>
                        <th style={{ textAlign: 'center', padding: '12px 8px', width: '40px' }}>
                          <input
                            type="checkbox"
                            style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                            title="Select all visible users"
                            onChange={(e) => {
                              // We'll populate this with the filtered user IDs via a data attribute
                              const checkboxes = document.querySelectorAll('.user-checkbox');
                              const userIds = Array.from(checkboxes).map(cb => parseInt(cb.dataset.userId));
                              if (e.target.checked) {
                                setSelectedUserIds(new Set([...selectedUserIds, ...userIds]));
                              } else {
                                const newSet = new Set(selectedUserIds);
                                userIds.forEach(id => newSet.delete(id));
                                setSelectedUserIds(newSet);
                              }
                            }}
                          />
                        </th>
                        {[
                          { key: 'username', label: 'Username' },
                          { key: 'email', label: 'Email' },
                          { key: 'venmo', label: 'Venmo' },
                          { key: 'pools', label: 'Pools' },
                          { key: 'joined', label: 'Created' }
                        ].map(col => (
                          <th
                            key={col.key}
                            onClick={() => {
                              if (userSortColumn === col.key) {
                                setUserSortDirection(userSortDirection === 'asc' ? 'desc' : 'asc');
                              } else {
                                setUserSortColumn(col.key);
                                setUserSortDirection('asc');
                              }
                            }}
                            style={{
                              textAlign: 'left',
                              padding: '12px 8px',
                              color: userSortColumn === col.key ? 'var(--primary)' : 'var(--text-muted)',
                              fontWeight: '600',
                              cursor: 'pointer',
                              userSelect: 'none'
                            }}
                          >
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                              {col.label}
                              {userSortColumn === col.key && (
                                <span style={{ fontSize: '10px' }}>
                                  {userSortDirection === 'asc' ? '▲' : '▼'}
                                </span>
                              )}
                            </span>
                          </th>
                        ))}
                        <th style={{ textAlign: 'center', padding: '12px 8px', color: 'var(--text-muted)', fontWeight: '600' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(() => {
                        // Build user-centric data: one row per user
                        const userMap = new Map();

                        // Add all pool members
                        poolsData.pools.forEach(pool => {
                          pool.members.forEach(member => {
                            if (!userMap.has(member.user_id)) {
                              userMap.set(member.user_id, {
                                user_id: member.user_id,
                                username: member.username,
                                display_name: member.display_name,
                                email: member.email,
                                venmo_handle: member.venmo_handle,
                                created_at: member.user_created_at,
                                pools: []
                              });
                            }
                            userMap.get(member.user_id).pools.push({
                              id: pool.id,
                              name: pool.name,
                              entry_fee: pool.entry_fee,
                              has_paid: member.has_paid,
                              isCommissioner: member.user_id === pool.commissioner_id
                            });
                          });
                        });

                        // Add users not in any pool
                        (poolsData.usersNotInPools || []).forEach(user => {
                          if (!userMap.has(user.user_id)) {
                            userMap.set(user.user_id, {
                              user_id: user.user_id,
                              username: user.username,
                              display_name: user.display_name,
                              email: user.email,
                              venmo_handle: user.venmo_handle,
                              created_at: user.created_at,
                              pools: []
                            });
                          }
                        });

                        // Convert to array and apply filters
                        let users = Array.from(userMap.values());

                        // Apply search filter
                        if (searchTerm) {
                          const term = searchTerm.toLowerCase();
                          users = users.filter(u =>
                            u.username?.toLowerCase().includes(term) ||
                            u.email?.toLowerCase().includes(term) ||
                            u.display_name?.toLowerCase().includes(term) ||
                            u.venmo_handle?.toLowerCase().includes(term) ||
                            u.pools.some(p => p.name.toLowerCase().includes(term))
                          );
                        }

                        // Apply pool filter
                        if (selectedPoolFilter === 'no-pool') {
                          users = users.filter(u => u.pools.length === 0);
                        } else if (selectedPoolFilter !== 'all-users' && selectedPoolFilter !== 'all-pools') {
                          users = users.filter(u => u.pools.some(p => String(p.id) === String(selectedPoolFilter)));
                        }

                        // Apply pool count filter
                        if (poolCountFilter !== 'any') {
                          if (poolCountFilter === '3+') {
                            users = users.filter(u => u.pools.length >= 3);
                          } else {
                            const count = parseInt(poolCountFilter, 10);
                            users = users.filter(u => u.pools.length === count);
                          }
                        }

                        // Apply joined date filter
                        if (joinedDateFilter !== 'all-time') {
                          const now = new Date();
                          let cutoffDate;
                          switch (joinedDateFilter) {
                            case 'hour':
                              cutoffDate = new Date(now.getTime() - 60 * 60 * 1000);
                              break;
                            case '6hours':
                              cutoffDate = new Date(now.getTime() - 6 * 60 * 60 * 1000);
                              break;
                            case 'today':
                              cutoffDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
                              break;
                            case 'week':
                              cutoffDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
                              break;
                            case 'month':
                              cutoffDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
                              break;
                            case '90days':
                              cutoffDate = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
                              break;
                            default:
                              cutoffDate = null;
                          }
                          if (cutoffDate) {
                            users = users.filter(u => u.created_at && new Date(u.created_at) >= cutoffDate);
                          }
                        }

                        // Sort by selected column
                        users.sort((a, b) => {
                          let aVal, bVal;
                          switch (userSortColumn) {
                            case 'email':
                              aVal = a.email || '';
                              bVal = b.email || '';
                              break;
                            case 'venmo':
                              aVal = a.venmo_handle || '';
                              bVal = b.venmo_handle || '';
                              break;
                            case 'pools':
                              aVal = a.pools.length;
                              bVal = b.pools.length;
                              break;
                            case 'joined':
                              aVal = a.created_at ? new Date(a.created_at).getTime() : 0;
                              bVal = b.created_at ? new Date(b.created_at).getTime() : 0;
                              break;
                            default: // username
                              aVal = a.username || '';
                              bVal = b.username || '';
                          }

                          let result;
                          if (typeof aVal === 'number') {
                            result = aVal - bVal;
                          } else {
                            result = aVal.localeCompare(bVal);
                          }

                          return userSortDirection === 'asc' ? result : -result;
                        });

                        return users.map(user => (
                          <tr key={user.user_id} style={{ borderBottom: '1px solid var(--border)' }}>
                            <td style={{ textAlign: 'center', padding: '12px 8px' }}>
                              <input
                                type="checkbox"
                                className="user-checkbox"
                                data-user-id={user.user_id}
                                checked={selectedUserIds.has(user.user_id)}
                                onChange={(e) => {
                                  const newSet = new Set(selectedUserIds);
                                  if (e.target.checked) {
                                    newSet.add(user.user_id);
                                  } else {
                                    newSet.delete(user.user_id);
                                  }
                                  setSelectedUserIds(newSet);
                                }}
                                style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                              />
                            </td>
                            <td style={{ padding: '12px 8px' }}>
                              <UserHoverCard userId={user.user_id} username={user.username}>
                                <span style={{ fontWeight: '500', cursor: 'pointer', color: 'var(--primary)' }}>{user.username || '—'}</span>
                              </UserHoverCard>
                              {user.display_name && user.display_name !== user.username && (
                                <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{user.display_name}</div>
                              )}
                            </td>
                            <td style={{ padding: '12px 8px' }}>
                              <a href={`mailto:${user.email}`} style={{ color: 'var(--primary)' }}>{user.email || '—'}</a>
                            </td>
                            <td style={{ padding: '12px 8px' }}>
                              {user.venmo_handle ? (
                                <a
                                  href={`https://venmo.com/u/${user.venmo_handle.replace('@', '')}`}
                                  target="_blank"
                                  rel="noreferrer"
                                  style={{ color: 'var(--primary)' }}
                                >
                                  {user.venmo_handle}
                                </a>
                              ) : '—'}
                            </td>
                            <td style={{ padding: '12px 8px' }}>
                              {user.pools.length === 0 ? (
                                <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>No pools</span>
                              ) : (
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                                  {user.pools.map(pool => (
                                    <span
                                      key={pool.id}
                                      style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '4px',
                                        background: pool.isCommissioner ? 'rgba(var(--primary-rgb), 0.2)' : 'var(--bg-dark)',
                                        padding: '2px 8px',
                                        borderRadius: '12px',
                                        fontSize: '12px',
                                        border: pool.has_paid ? '1px solid #22c55e' : '1px solid var(--border)'
                                      }}
                                      title={`${pool.name} - $${pool.entry_fee} - ${pool.has_paid ? 'Paid' : 'Unpaid'}${pool.isCommissioner ? ' (Commissioner)' : ''}`}
                                    >
                                      {pool.name}
                                      {pool.isCommissioner && <span style={{ color: 'var(--primary)', fontSize: '10px' }}>★</span>}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </td>
                            <td style={{ padding: '12px 8px', fontSize: '12px', color: 'var(--text-muted)' }}>
                              {user.created_at ? new Date(user.created_at).toLocaleDateString() : '—'}
                            </td>
                            <td style={{ textAlign: 'center', padding: '12px 8px' }}>
                              <button
                                className="btn"
                                style={{ padding: '4px 8px', fontSize: '11px', background: '#ef4444', color: 'white' }}
                                onClick={async () => {
                                  if (!window.confirm(`Delete user "${user.username}"? This will remove all their data and any pools they own.`)) return;
                                  try {
                                    await adminDeleteUser(user.user_id);
                                    showMessage('success', 'User deleted');
                                    loadPoolsData();
                                  } catch (err) {
                                    showMessage('error', 'Failed to delete user');
                                  }
                                }}
                              >
                                Delete
                              </button>
                            </td>
                          </tr>
                        ));
                      })()}
                    </tbody>
                  </table>
                </div>

                {/* Export emails button */}
                <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                  <button
                    className="btn btn-secondary"
                    onClick={() => {
                      // Build user map for deduplication
                      const userMap = new Map();
                      poolsData.pools.forEach(pool => {
                        pool.members.forEach(m => {
                          if (m.email) userMap.set(m.user_id, m.email);
                        });
                      });
                      (poolsData.usersNotInPools || []).forEach(u => {
                        if (u.email) userMap.set(u.user_id, u.email);
                      });

                      let emails;

                      // If users are selected, only copy their emails
                      if (selectedUserIds.size > 0) {
                        emails = Array.from(userMap.entries())
                          .filter(([id]) => selectedUserIds.has(id))
                          .map(([, email]) => email);
                      } else {
                        // Otherwise use filter-based logic
                        emails = Array.from(userMap.values());

                        // Apply filter
                        if (selectedPoolFilter === 'no-pool') {
                          const noPoolUserIds = new Set((poolsData.usersNotInPools || []).map(u => u.user_id));
                          emails = Array.from(userMap.entries())
                            .filter(([id]) => noPoolUserIds.has(id))
                            .map(([, email]) => email);
                        } else if (selectedPoolFilter !== 'all-users' && selectedPoolFilter !== 'all-pools') {
                          const pool = poolsData.pools.find(p => String(p.id) === String(selectedPoolFilter));
                          if (pool) {
                            emails = pool.members.map(m => m.email).filter(Boolean);
                          }
                        }
                      }

                      navigator.clipboard.writeText(emails.join(', '));
                      showMessage('success', `Copied ${emails.length} emails to clipboard`);
                    }}
                  >
                    {selectedUserIds.size > 0 ? `Copy ${selectedUserIds.size} Selected Emails` : 'Copy Emails to Clipboard'}
                  </button>
                  {selectedUserIds.size > 0 && (
                    <button
                      className="btn"
                      style={{ padding: '8px 12px', fontSize: '13px', background: 'transparent', border: '1px solid var(--border)', color: 'var(--text-muted)' }}
                      onClick={() => setSelectedUserIds(new Set())}
                    >
                      Clear Selection
                    </button>
                  )}
                </div>
              </div>
            </>
          ) : (
            <div className="card" style={{ textAlign: 'center', padding: '48px' }}>
              <p style={{ color: 'var(--text-muted)' }}>No data loaded. Click Refresh to load pools data.</p>
              <button className="btn btn-primary" style={{ marginTop: '16px' }} onClick={loadPoolsData}>
                Load Data
              </button>
            </div>
          )}
        </div>
      )}

      {/* Activity Tab */}
      {activeTab === 'activity' && (
        <div>
          {/* Activity Stats */}
          {activityStats && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '24px' }}>
              <div className="card" style={{ textAlign: 'center' }}>
                <p style={{ color: 'var(--text-muted)', fontSize: '14px', marginBottom: '4px' }}>Today</p>
                <p style={{ fontSize: '32px', fontWeight: '700', color: 'var(--primary)' }}>{activityStats.today}</p>
              </div>
              <div className="card" style={{ textAlign: 'center' }}>
                <p style={{ color: 'var(--text-muted)', fontSize: '14px', marginBottom: '4px' }}>This Week</p>
                <p style={{ fontSize: '32px', fontWeight: '700' }}>{activityStats.thisWeek}</p>
              </div>
              <div className="card" style={{ textAlign: 'center' }}>
                <p style={{ color: 'var(--text-muted)', fontSize: '14px', marginBottom: '4px' }}>All Time</p>
                <p style={{ fontSize: '32px', fontWeight: '700' }}>{activityStats.total}</p>
              </div>
            </div>
          )}

          {/* Activity Filters */}
          <div className="card" style={{ marginBottom: '24px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
              <h3 style={{ margin: 0 }}>Activity Log</h3>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <select
                  className="form-input"
                  value={activityFilter}
                  onChange={(e) => {
                    setActivityFilter(e.target.value);
                    loadActivityLogs(e.target.value);
                  }}
                  style={{ padding: '8px 12px', minWidth: '180px' }}
                >
                  <option value="all">All Activity</option>
                  <option value="user_registered">User Registrations</option>
                  <option value="pool_created">Pool Created</option>
                  <option value="pool_member_joined">Pool Joins</option>
                  <option value="picks_submitted">Picks Submitted</option>
                  <option value="event_status_changed">Event Status Changes</option>
                  <option value="fight_result_updated">Fight Results</option>
                  <option value="admin_login">Admin Logins</option>
                </select>
                <button
                  className="btn btn-secondary"
                  onClick={() => loadActivityLogs(activityFilter)}
                  disabled={activityLoading}
                >
                  {activityLoading ? 'Loading...' : 'Refresh'}
                </button>
              </div>
            </div>
          </div>

          {/* Activity List */}
          {activityLoading ? (
            <Loading />
          ) : activityLogs.length === 0 ? (
            <div className="card" style={{ textAlign: 'center', padding: '40px' }}>
              <p style={{ color: 'var(--text-muted)' }}>No activity logs found.</p>
              <p style={{ color: 'var(--text-muted)', fontSize: '14px', marginTop: '8px' }}>
                Activity logging needs to be enabled. Run the SQL migration to create the activity_logs table.
              </p>
            </div>
          ) : (
            <div className="card" style={{ padding: 0 }}>
              <div style={{ maxHeight: '600px', overflowY: 'auto' }}>
                {activityLogs.map((log, idx) => {
                  const getActionIcon = (type) => {
                    if (type.includes('user_registered')) return '👤';
                    if (type.includes('pool_created')) return '🏊';
                    if (type.includes('pool_member')) return '🤝';
                    if (type.includes('picks')) return '🎯';
                    if (type.includes('event')) return '📅';
                    if (type.includes('fight')) return '🥊';
                    if (type.includes('admin')) return '🔐';
                    return '📝';
                  };

                  const getActionColor = (type) => {
                    if (type.includes('user_registered')) return '#22c55e';
                    if (type.includes('pool_created')) return '#3b82f6';
                    if (type.includes('pool_member')) return '#8b5cf6';
                    if (type.includes('picks')) return '#f59e0b';
                    if (type.includes('event')) return '#ec4899';
                    if (type.includes('fight')) return '#ef4444';
                    if (type.includes('admin')) return '#6366f1';
                    return 'var(--text-muted)';
                  };

                  return (
                    <div
                      key={log.id || idx}
                      style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '12px',
                        padding: '12px 16px',
                        borderBottom: idx < activityLogs.length - 1 ? '1px solid var(--border)' : 'none',
                        background: idx % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.02)'
                      }}
                    >
                      <div style={{
                        width: '36px',
                        height: '36px',
                        borderRadius: '50%',
                        background: `${getActionColor(log.action_type)}20`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0
                      }}>
                        <span style={{ fontSize: '16px' }}>{getActionIcon(log.action_type)}</span>
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{ margin: 0, fontSize: '14px' }}>
                          {log.description || log.action_type.replace(/_/g, ' ')}
                        </p>
                        <div style={{ display: 'flex', gap: '12px', marginTop: '4px', fontSize: '12px', color: 'var(--text-muted)' }}>
                          <span style={{
                            background: `${getActionColor(log.action_type)}20`,
                            color: getActionColor(log.action_type),
                            padding: '2px 6px',
                            borderRadius: '4px',
                            fontWeight: '500'
                          }}>
                            {log.action_type.replace(/_/g, ' ')}
                          </span>
                          {log.user?.username && (
                            <span>by <UserHoverCard userId={log.user.id} username={log.user.username}>
                              <span style={{ cursor: 'pointer', color: 'var(--primary)' }}>{log.user.username}</span>
                            </UserHoverCard></span>
                          )}
                        </div>
                      </div>
                      <div style={{ color: 'var(--text-muted)', fontSize: '12px', flexShrink: 0 }}>
                        {formatTime(log.created_at)}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Chat Review Tab (read-only) */}
      {activeTab === 'chat' && (
        <div>
          <div className="card" style={{ marginBottom: '16px' }}>
            <h3 style={{ margin: 0, marginBottom: '4px' }}>Chat Review</h3>
            <p style={{ color: 'var(--text-muted)', margin: 0, fontSize: '13px' }}>
              Read-only view of every pool's Corner Chat. Use it to spot abuse, harassment, or anything else that violates the rules.
            </p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(260px, 340px) 1fr', gap: '16px', alignItems: 'flex-start' }}>
            {/* Pool list */}
            <div className="card" style={{ padding: '12px' }}>
              <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
                <input
                  className="form-input"
                  placeholder="Filter pools..."
                  value={chatPoolFilter}
                  onChange={(e) => setChatPoolFilter(e.target.value)}
                  style={{ flex: 1 }}
                />
                <button className="btn btn-secondary" onClick={loadChatPools} disabled={chatPoolsLoading}>
                  {chatPoolsLoading ? '...' : 'Refresh'}
                </button>
              </div>

              {chatPoolsLoading && chatPools.length === 0 ? (
                <Loading />
              ) : chatPools.length === 0 ? (
                <p style={{ color: 'var(--text-muted)', fontSize: '13px', margin: 0 }}>No pool chat activity yet.</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', maxHeight: '70vh', overflowY: 'auto' }}>
                  {chatPools
                    .filter(p => !chatPoolFilter || p.league_name.toLowerCase().includes(chatPoolFilter.toLowerCase()))
                    .map(p => {
                      const isSelected = selectedChatPool?.league_id === p.league_id;
                      return (
                        <button
                          key={p.league_id}
                          onClick={() => openChatPool(p)}
                          style={{
                            textAlign: 'left',
                            padding: '10px 12px',
                            borderRadius: '8px',
                            border: '1px solid var(--border)',
                            background: isSelected ? 'rgba(251, 191, 36, 0.12)' : 'transparent',
                            color: 'inherit',
                            cursor: 'pointer',
                          }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '8px' }}>
                            <span style={{ fontWeight: 600, fontSize: '14px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {p.is_global ? '🏆 ' : ''}{p.league_name}
                            </span>
                            <span style={{ color: 'var(--text-muted)', fontSize: '11px', flexShrink: 0 }}>
                              {p.message_count}
                            </span>
                          </div>
                          <div style={{ color: 'var(--text-muted)', fontSize: '12px', marginTop: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            <strong>{p.last_message_username}:</strong> {p.last_message_body}
                          </div>
                          <div style={{ color: 'var(--text-muted)', fontSize: '11px', marginTop: '2px' }}>
                            {formatTime(p.last_message_at)}
                          </div>
                        </button>
                      );
                    })}
                </div>
              )}
            </div>

            {/* Message thread */}
            <div className="card" style={{ padding: '12px', minHeight: '200px' }}>
              {!selectedChatPool ? (
                <p style={{ color: 'var(--text-muted)', margin: 0 }}>Select a pool to view its chat.</p>
              ) : (
                <>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '8px', marginBottom: '12px', flexWrap: 'wrap' }}>
                    <h3 style={{ margin: 0 }}>
                      {selectedChatPool.is_global ? '🏆 ' : ''}{selectedChatPool.league_name}
                    </h3>
                    <button
                      className="btn btn-secondary"
                      onClick={() => openChatPool(selectedChatPool)}
                      disabled={chatMessagesLoading}
                    >
                      {chatMessagesLoading ? 'Loading...' : 'Refresh'}
                    </button>
                  </div>

                  {chatMessagesLoading ? (
                    <Loading />
                  ) : chatMessages.length === 0 ? (
                    <p style={{ color: 'var(--text-muted)', margin: 0 }}>No messages.</p>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '70vh', overflowY: 'auto' }}>
                      {chatMessages.map(m => (
                        <div
                          key={m.id}
                          style={{
                            padding: '10px 12px',
                            borderRadius: '8px',
                            border: '1px solid var(--border)',
                            background: 'rgba(255,255,255,0.02)',
                          }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '8px', marginBottom: '4px' }}>
                            <UserHoverCard userId={m.user_id} username={m.username}>
                              <span style={{ fontWeight: 600, color: 'var(--primary)', cursor: 'pointer' }}>
                                {m.username}
                                {m.display_name && m.display_name !== m.username && (
                                  <span style={{ color: 'var(--text-muted)', fontWeight: 400, marginLeft: '6px', fontSize: '12px' }}>
                                    ({m.display_name})
                                  </span>
                                )}
                              </span>
                            </UserHoverCard>
                            <span style={{ color: 'var(--text-muted)', fontSize: '11px', flexShrink: 0 }}>
                              {formatTime(m.created_at)}
                            </span>
                          </div>
                          <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: '14px' }}>
                            {m.body}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Notifications Tab */}
      {activeTab === 'notifications' && (
        <div>
          <div className="card" style={{ marginBottom: '16px' }}>
            <h3 style={{ margin: 0, marginBottom: '4px' }}>Push Notifications</h3>
            <p style={{ color: 'var(--text-muted)', margin: 0, fontSize: '13px' }}>
              Compose one-off sends or manage recurring rules. Delivery is dormant until APNS/FCM
              credentials are added (see CLAUDE.md) — sends here are logged and staged, but won't
              reach devices until then.
            </p>
          </div>

          <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
            {['send', 'scheduled', 'logs'].map(sub => (
              <button
                key={sub}
                className={`btn ${notifSubTab === sub ? 'btn-primary' : 'btn-secondary'}`}
                onClick={() => setNotifSubTab(sub)}
                style={{ textTransform: 'capitalize' }}
              >
                {sub === 'send' ? 'Send Now' : sub}
              </button>
            ))}
          </div>

          {/* Send Now */}
          {notifSubTab === 'send' && (
            <div className="card" style={{ maxWidth: '520px' }}>
              <div className="form-group">
                <label>Title</label>
                <input
                  className="form-input"
                  value={notifTitle}
                  onChange={(e) => setNotifTitle(e.target.value.slice(0, 65))}
                  placeholder="Fight night is here!"
                  maxLength={65}
                />
                <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}>{notifTitle.length}/65</div>
              </div>
              <div className="form-group">
                <label>Body</label>
                <textarea
                  className="form-input"
                  value={notifBody}
                  onChange={(e) => setNotifBody(e.target.value.slice(0, 178))}
                  placeholder="Picks lock in 1 hour — don't miss out."
                  maxLength={178}
                  rows={3}
                />
                <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}>{notifBody.length}/178</div>
              </div>
              <div className="form-group">
                <label>Audience</label>
                <select
                  className="form-input"
                  value={notifAudienceType}
                  onChange={(e) => setNotifAudienceType(e.target.value)}
                >
                  <option value="all">Everyone (all registered users)</option>
                  <option value="pool">Specific pool members</option>
                  <option value="pending_picks">Users who haven't made picks yet (current event)</option>
                </select>
              </div>
              {notifAudienceType === 'pool' && (
                <div className="form-group">
                  <label>Pool</label>
                  <select
                    className="form-input"
                    value={notifAudiencePoolId}
                    onChange={(e) => setNotifAudiencePoolId(e.target.value)}
                  >
                    <option value="">Select a pool...</option>
                    {chatPools.map(p => (
                      <option key={p.league_id} value={p.league_id}>
                        {p.is_global ? '🏆 ' : ''}{p.league_name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <button
                className="btn btn-primary"
                onClick={handleSendNotification}
                disabled={notifSending}
                style={{ marginTop: '8px' }}
              >
                {notifSending ? 'Sending...' : 'Send Now'}
              </button>
            </div>
          )}

          {/* Scheduled Rules */}
          {notifSubTab === 'scheduled' && (
            <div>
              <div style={{ marginBottom: '12px' }}>
                <button className="btn btn-primary" onClick={() => setShowScheduleForm(v => !v)}>
                  {showScheduleForm ? 'Cancel' : '+ New Scheduled Notification'}
                </button>
              </div>

              {showScheduleForm && (
                <div className="card" style={{ maxWidth: '520px', marginBottom: '16px' }}>
                  <div className="form-group">
                    <label>Title</label>
                    <input
                      className="form-input"
                      value={scheduleForm.title}
                      onChange={(e) => setScheduleForm(f => ({ ...f, title: e.target.value.slice(0, 65) }))}
                      maxLength={65}
                    />
                  </div>
                  <div className="form-group">
                    <label>Body</label>
                    <textarea
                      className="form-input"
                      value={scheduleForm.body}
                      onChange={(e) => setScheduleForm(f => ({ ...f, body: e.target.value.slice(0, 178) }))}
                      maxLength={178}
                      rows={3}
                    />
                  </div>
                  <div className="form-group">
                    <label>Audience</label>
                    <select
                      className="form-input"
                      value={scheduleForm.audienceType}
                      onChange={(e) => setScheduleForm(f => ({ ...f, audienceType: e.target.value }))}
                    >
                      <option value="all">Everyone (all registered users)</option>
                      <option value="pool">Specific pool members</option>
                      <option value="pending_picks">Users who haven't made picks yet (current event)</option>
                    </select>
                  </div>
                  {scheduleForm.audienceType === 'pool' && (
                    <div className="form-group">
                      <label>Pool</label>
                      <select
                        className="form-input"
                        value={scheduleForm.audiencePoolId}
                        onChange={(e) => setScheduleForm(f => ({ ...f, audiencePoolId: e.target.value }))}
                      >
                        <option value="">Select a pool...</option>
                        {chatPools.map(p => (
                          <option key={p.league_id} value={p.league_id}>
                            {p.is_global ? '🏆 ' : ''}{p.league_name}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                  <div className="form-group">
                    <label>Trigger</label>
                    <select
                      className="form-input"
                      value={scheduleForm.triggerType}
                      onChange={(e) => setScheduleForm(f => ({ ...f, triggerType: e.target.value }))}
                    >
                      <option value="daily">Daily, at a fixed time</option>
                      <option value="weekly">Weekly, on a specific day</option>
                      <option value="event_lock_reminder">Before picks lock (per event)</option>
                    </select>
                  </div>
                  {(scheduleForm.triggerType === 'daily' || scheduleForm.triggerType === 'weekly') && (
                    <div className="form-group">
                      <label>Time (UTC, HH:MM)</label>
                      <input
                        type="time"
                        className="form-input"
                        value={scheduleForm.triggerTime}
                        onChange={(e) => setScheduleForm(f => ({ ...f, triggerTime: e.target.value }))}
                      />
                    </div>
                  )}
                  {scheduleForm.triggerType === 'weekly' && (
                    <div className="form-group">
                      <label>Day of week</label>
                      <select
                        className="form-input"
                        value={scheduleForm.triggerDay}
                        onChange={(e) => setScheduleForm(f => ({ ...f, triggerDay: e.target.value }))}
                      >
                        {['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].map((d, i) => (
                          <option key={i} value={i}>{d}</option>
                        ))}
                      </select>
                    </div>
                  )}
                  {scheduleForm.triggerType === 'event_lock_reminder' && (
                    <div className="form-group">
                      <label>Hours before lock</label>
                      <input
                        type="number"
                        min={1}
                        className="form-input"
                        value={scheduleForm.hoursBeforeLock}
                        onChange={(e) => setScheduleForm(f => ({ ...f, hoursBeforeLock: e.target.value }))}
                      />
                    </div>
                  )}
                  <button
                    className="btn btn-primary"
                    onClick={handleCreateScheduledNotification}
                    disabled={scheduleSaving}
                    style={{ marginTop: '8px' }}
                  >
                    {scheduleSaving ? 'Saving...' : 'Create Rule'}
                  </button>
                </div>
              )}

              {scheduledLoading ? (
                <Loading />
              ) : scheduledNotifications.length === 0 ? (
                <p style={{ color: 'var(--text-muted)' }}>No scheduled notifications yet.</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {scheduledNotifications.map(rule => (
                    <div key={rule.id} className="card" style={{ padding: '12px 16px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', flexWrap: 'wrap' }}>
                        <div>
                          <div style={{ fontWeight: 600 }}>{rule.title}</div>
                          <div style={{ color: 'var(--text-muted)', fontSize: '13px', marginTop: '2px' }}>{rule.body}</div>
                          <div style={{ color: 'var(--text-muted)', fontSize: '12px', marginTop: '6px' }}>
                            {rule.trigger_type === 'daily' && `Daily at ${rule.trigger_time} UTC`}
                            {rule.trigger_type === 'weekly' && `Weekly on ${['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][rule.trigger_day]} at ${rule.trigger_time} UTC`}
                            {rule.trigger_type === 'event_lock_reminder' && `${rule.hours_before_lock}h before picks lock`}
                            {' · '}
                            {rule.audience_type === 'all' && 'Everyone'}
                            {rule.audience_type === 'pool' && `Pool: ${rule.pool?.name || rule.audience_pool_id}`}
                            {rule.audience_type === 'pending_picks' && 'Pending picks'}
                            {' · '}
                            Sent {rule.total_sent}x
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
                          <button
                            className={`btn ${rule.is_enabled ? 'btn-secondary' : 'btn-primary'}`}
                            onClick={() => toggleScheduledNotification(rule)}
                          >
                            {rule.is_enabled ? 'Disable' : 'Enable'}
                          </button>
                          <button className="btn btn-secondary" onClick={() => handleDeleteScheduledNotification(rule)}>
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Logs */}
          {notifSubTab === 'logs' && (
            <div>
              {notificationLogsLoading ? (
                <Loading />
              ) : notificationLogs.length === 0 ? (
                <p style={{ color: 'var(--text-muted)' }}>No notifications sent yet.</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {notificationLogs.map(log => (
                    <div key={log.id} className="card" style={{ padding: '12px 16px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '8px', flexWrap: 'wrap' }}>
                        <div style={{ fontWeight: 600 }}>{log.title}</div>
                        <div style={{ color: 'var(--text-muted)', fontSize: '12px' }}>{formatTime(log.created_at)}</div>
                      </div>
                      <div style={{ color: 'var(--text-muted)', fontSize: '13px', marginTop: '2px' }}>{log.body}</div>
                      <div style={{ color: 'var(--text-muted)', fontSize: '12px', marginTop: '6px' }}>
                        {log.audience_type === 'all' && 'Everyone'}
                        {log.audience_type === 'pool' && `Pool: ${log.pool?.name || log.audience_pool_id}`}
                        {log.audience_type === 'pending_picks' && 'Pending picks'}
                        {' · '}
                        {log.sent_count}/{log.total_count} dispatched
                        {' · '}
                        by {log.sent_by}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Duplicates Tab */}
      {activeTab === 'duplicates' && (
        <div>
          <div className="card" style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
              <div>
                <h3 style={{ marginBottom: 4 }}>Duplicate Events</h3>
                <p style={{ color: 'var(--text-muted)', fontSize: 13, margin: 0 }}>
                  Events sharing a calendar day. Pick which row to keep — fights, picks, scores, and badges from the other row will be moved onto the kept row, then the other row is deleted.
                </p>
              </div>
              <button className="btn btn-secondary" onClick={loadDuplicates} disabled={duplicatesLoading}>
                {duplicatesLoading ? 'Loading…' : 'Refresh'}
              </button>
            </div>
          </div>

          {duplicatesLoading && duplicateGroups.length === 0 ? (
            <Loading />
          ) : duplicateGroups.length === 0 ? (
            <div className="card" style={{ textAlign: 'center', padding: 32 }}>
              <p style={{ color: 'var(--text-muted)', margin: 0 }}>No duplicate events found.</p>
            </div>
          ) : (
            duplicateGroups.map((group) => (
              <div key={group.day} className="card" style={{ marginBottom: 16 }}>
                <div style={{ marginBottom: 12, fontWeight: 700, fontSize: 14, color: 'var(--text-muted)' }}>
                  {group.day} ({group.events.length} events)
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {group.events.map((ev) => {
                    const otherIds = group.events.filter((e) => e.id !== ev.id).map((e) => e.id);
                    return (
                      <div
                        key={ev.id}
                        style={{
                          padding: 12,
                          border: '1px solid var(--border)',
                          borderRadius: 10,
                          background: 'var(--bg-card-hover)',
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
                          <div style={{ minWidth: 0, flex: 1 }}>
                            <div style={{ fontWeight: 700, marginBottom: 4 }}>
                              #{ev.id} — {ev.name}
                            </div>
                            <div style={{ fontSize: 12, color: 'var(--text-muted)', display: 'flex', gap: 14, flexWrap: 'wrap' }}>
                              <span>{new Date(ev.date).toISOString()}</span>
                              {ev.venue && <span>{ev.venue}</span>}
                              <span>{ev.status}</span>
                              <span>{ev.fightCount} fights</span>
                              <span>{ev.pickCount} picks</span>
                              <span>{ev.scoreRowCount} score rows</span>
                            </div>
                          </div>
                          <div style={{ display: 'flex', gap: 6 }}>
                            {otherIds.map((dropId) => {
                              const drop = group.events.find((e) => e.id === dropId);
                              return (
                                <button
                                  key={dropId}
                                  className="btn btn-primary"
                                  style={{ fontSize: 12, padding: '6px 12px' }}
                                  disabled={merging}
                                  onClick={() => handleMergeDuplicates(ev.id, dropId, drop?.name || `#${dropId}`)}
                                >
                                  Keep this, merge #{dropId}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Settings Tab */}
      {activeTab === 'settings' && (
        <div>
          <div className="card" style={{ marginBottom: '24px' }}>
            <h3 style={{ marginBottom: '16px' }}>Two-Factor Authentication</h3>
            <p style={{ color: 'var(--text-muted)', marginBottom: '16px' }}>
              Add an extra layer of security to your admin account using an authenticator app like Google Authenticator or Authy.
            </p>

            {twoFactorEnabled ? (
              <div>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '12px 16px',
                  background: 'rgba(34, 197, 94, 0.1)',
                  border: '1px solid rgba(34, 197, 94, 0.3)',
                  borderRadius: '8px',
                  marginBottom: '24px'
                }}>
                  <span style={{ color: '#22c55e', fontSize: '18px' }}>&#10003;</span>
                  <span style={{ color: '#22c55e', fontWeight: '500' }}>2FA is enabled</span>
                </div>

                <h4 style={{ marginBottom: '12px' }}>Disable 2FA</h4>
                <p style={{ color: 'var(--text-muted)', fontSize: '14px', marginBottom: '16px' }}>
                  To disable two-factor authentication, enter your password and current verification code.
                </p>
                <form onSubmit={handleDisable2FA}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px', marginBottom: '16px' }}>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label className="form-label">Password</label>
                      <input
                        type="password"
                        className="form-input"
                        value={disable2FAPassword}
                        onChange={(e) => setDisable2FAPassword(e.target.value)}
                        required
                      />
                    </div>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label className="form-label">Verification Code</label>
                      <input
                        type="text"
                        className="form-input"
                        value={disable2FACode}
                        onChange={(e) => setDisable2FACode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        placeholder="000000"
                        maxLength="6"
                        required
                      />
                    </div>
                  </div>
                  <button
                    type="submit"
                    className="btn"
                    disabled={twoFALoading || !disable2FAPassword || disable2FACode.length !== 6}
                    style={{ background: '#ef4444', color: 'white' }}
                  >
                    {twoFALoading ? 'Disabling...' : 'Disable 2FA'}
                  </button>
                </form>
              </div>
            ) : setup2FAData ? (
              <div>
                <div style={{
                  background: 'var(--bg-dark)',
                  border: '1px solid var(--border)',
                  borderRadius: '12px',
                  padding: '24px',
                  textAlign: 'center',
                  marginBottom: '24px'
                }}>
                  <h4 style={{ marginBottom: '16px' }}>Scan this QR code with your authenticator app</h4>
                  <img
                    src={setup2FAData.qrCode}
                    alt="2FA QR Code"
                    style={{ maxWidth: '200px', borderRadius: '8px', marginBottom: '16px' }}
                  />
                  <p style={{ color: 'var(--text-muted)', fontSize: '13px', marginBottom: '8px' }}>
                    Or enter this key manually:
                  </p>
                  <code style={{
                    display: 'block',
                    background: 'var(--bg-input)',
                    padding: '8px 16px',
                    borderRadius: '6px',
                    fontSize: '14px',
                    letterSpacing: '2px',
                    wordBreak: 'break-all'
                  }}>
                    {setup2FAData.secret}
                  </code>
                </div>

                <form onSubmit={handleVerify2FASetup}>
                  <div className="form-group">
                    <label className="form-label">Enter the 6-digit code from your app to verify</label>
                    <input
                      type="text"
                      className="form-input"
                      value={setup2FACode}
                      onChange={(e) => setSetup2FACode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="000000"
                      style={{ textAlign: 'center', fontSize: '20px', letterSpacing: '6px', maxWidth: '200px' }}
                      maxLength="6"
                      autoFocus
                      required
                    />
                  </div>
                  <div style={{ display: 'flex', gap: '12px' }}>
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={twoFALoading || setup2FACode.length !== 6}
                    >
                      {twoFALoading ? 'Verifying...' : 'Verify & Enable 2FA'}
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => {
                        setSetup2FAData(null);
                        setSetup2FACode('');
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            ) : (
              <div>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '12px 16px',
                  background: 'rgba(251, 191, 36, 0.1)',
                  border: '1px solid rgba(251, 191, 36, 0.3)',
                  borderRadius: '8px',
                  marginBottom: '24px'
                }}>
                  <span style={{ color: '#fbbf24', fontSize: '18px' }}>&#9888;</span>
                  <span style={{ color: '#fbbf24', fontWeight: '500' }}>2FA is not enabled</span>
                </div>
                <button
                  className="btn btn-primary"
                  onClick={handleSetup2FA}
                  disabled={twoFALoading}
                >
                  {twoFALoading ? 'Setting up...' : 'Set Up 2FA'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {!selectedEvent && activeTab !== 'cards' && activeTab !== 'users' && activeTab !== 'settings' && activeTab !== 'pools' && activeTab !== 'chat' && activeTab !== 'notifications' && activeTab !== 'activity' && activeTab !== 'duplicates' && activeTab !== 'leaderboard' && (
        <div className="card" style={{ textAlign: 'center' }}>
          <p style={{ color: 'var(--text-muted)' }}>
            Please create or select an event first.
          </p>
          {allEvents.length > 0 && (
            <button
              className="btn btn-primary"
              style={{ marginTop: '16px' }}
              onClick={() => loadEventDetails(allEvents[0].id)}
            >
              Load {allEvents[0].name}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// Sub-component for compact fight row with expandable edit
function FightRowCompact({ fight, expanded, onToggle, onCancel, onDelete, onUpdate }) {
  const hasResult = !!fight.winner;
  const [localRounds, setLocalRounds] = useState(fight.rounds ?? (fight.is_title_fight ? 5 : 3));
  const [localIsTitle, setLocalIsTitle] = useState(fight.is_title_fight === 1);
  const [localCardType, setLocalCardType] = useState(fight.card_type || 'prelim');

  // Reset local state when fight changes
  useEffect(() => {
    setLocalRounds(fight.rounds ?? (fight.is_title_fight ? 5 : 3));
    setLocalIsTitle(fight.is_title_fight === 1);
    setLocalCardType(fight.card_type || 'prelim');
  }, [fight.id, fight.rounds, fight.is_title_fight, fight.card_type]);

  const handleSaveDetails = () => {
    onUpdate({
      rounds: localRounds,
      is_title_fight: localIsTitle,
      card_type: localCardType
    });
  };

  const hasChanges = localRounds !== (fight.rounds ?? (fight.is_title_fight ? 5 : 3)) ||
                     localIsTitle !== (fight.is_title_fight === 1) ||
                     localCardType !== (fight.card_type || 'prelim');

  return (
    <div className="card" style={{ marginBottom: '8px', padding: 0, overflow: 'hidden' }}>
      {/* Collapsed row - always visible */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '12px 16px',
          opacity: fight.is_cancelled ? 0.6 : 1,
          background: hasResult ? 'rgba(34, 197, 94, 0.05)' : 'transparent'
        }}
      >
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <strong style={{
              textDecoration: fight.is_cancelled ? 'line-through' : 'none',
              fontWeight: hasResult && fight.winner === fight.fighter1_name ? '700' : '500'
            }}>
              {fight.fighter1_name}
            </strong>
            <span style={{ color: 'var(--text-muted)', fontSize: '13px' }}>vs</span>
            <strong style={{
              textDecoration: fight.is_cancelled ? 'line-through' : 'none',
              fontWeight: hasResult && fight.winner === fight.fighter2_name ? '700' : '500'
            }}>
              {fight.fighter2_name}
            </strong>
            {fight.is_title_fight === 1 && (
              <span style={{
                background: 'rgba(251, 191, 36, 0.2)',
                color: '#fbbf24',
                padding: '2px 8px',
                borderRadius: '10px',
                fontSize: '11px',
                fontWeight: '600'
              }}>
                TITLE
              </span>
            )}
            <span style={{
              background: 'var(--bg-input)',
              color: 'var(--text-muted)',
              padding: '2px 6px',
              borderRadius: '8px',
              fontSize: '10px',
              fontWeight: '500'
            }}>
              {fight.rounds ?? (fight.is_title_fight ? 5 : 3)} RDS
            </span>
            {fight.is_cancelled === 1 && (
              <span style={{
                background: 'rgba(239, 68, 68, 0.2)',
                color: '#ef4444',
                padding: '2px 8px',
                borderRadius: '10px',
                fontSize: '11px',
                fontWeight: '600'
              }}>
                CANCELLED
              </span>
            )}
            {hasResult && (
              <span style={{
                background: 'rgba(34, 197, 94, 0.2)',
                color: '#22c55e',
                padding: '2px 8px',
                borderRadius: '10px',
                fontSize: '11px',
                fontWeight: '500'
              }}>
                {fight.method || 'Result set'}
              </span>
            )}
          </div>
          <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '4px' }}>
            {fight.weight_class}
          </div>
        </div>
        <button
          className="btn btn-secondary"
          onClick={onToggle}
          style={{ padding: '6px 12px', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '4px' }}
        >
          Edit
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
            style={{ transition: 'transform 0.2s', transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)' }}>
            <polyline points="6 9 12 15 18 9"></polyline>
          </svg>
        </button>
      </div>

      {/* Expanded edit controls */}
      {expanded && (
        <div style={{
          padding: '12px 16px',
          borderTop: '1px solid var(--border)',
          background: 'var(--bg-dark)'
        }}>
          {/* Fight settings row */}
          <div style={{ display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <label style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Rounds:</label>
              <select
                className="form-input"
                value={localRounds}
                onChange={(e) => setLocalRounds(parseInt(e.target.value))}
                style={{ width: '70px', padding: '4px 8px', fontSize: '12px' }}
              >
                <option value={3}>3</option>
                <option value={5}>5</option>
              </select>
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={localIsTitle}
                onChange={(e) => setLocalIsTitle(e.target.checked)}
              />
              Championship Fight
            </label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <label style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Card:</label>
              <select
                className="form-input"
                value={localCardType}
                onChange={(e) => setLocalCardType(e.target.value)}
                style={{ width: '120px', padding: '4px 8px', fontSize: '12px' }}
              >
                <option value="main">Main Card</option>
                <option value="prelim">Prelims</option>
                <option value="early">Early Prelims</option>
              </select>
            </div>
            {hasChanges && (
              <button
                className="btn btn-primary"
                onClick={handleSaveDetails}
                style={{ padding: '4px 12px', fontSize: '12px' }}
              >
                Save
              </button>
            )}
          </div>

          {/* Action buttons row */}
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <button
              className="btn btn-secondary"
              onClick={() => onCancel(fight)}
              style={{ padding: '6px 12px', fontSize: '12px' }}
            >
              {fight.is_cancelled ? 'Restore Fight' : 'Cancel Fight'}
            </button>
            <button
              className="btn"
              onClick={() => onDelete(fight.id)}
              style={{ background: '#ef4444', color: 'white', padding: '6px 12px', fontSize: '12px' }}
            >
              Delete Fight
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// Sub-component for expandable fight result row
function ExpandableFightResult({ fight, expanded, onToggle, onUpdateResult, requireMethod }) {
  const [winner, setWinner] = useState(fight.winner || '');
  const [method, setMethod] = useState(fight.method || '');

  const isDraw = winner === 'Draw';

  const handleSave = () => {
    if (!winner) return;
    if (!isDraw && requireMethod && !method) return;
    onUpdateResult(fight.id, winner, isDraw ? 'Draw' : (method || null));
  };

  const hasResult = !!fight.winner;

  return (
    <div style={{
      border: '1px solid var(--border)',
      borderRadius: '6px',
      marginBottom: '8px',
      overflow: 'hidden'
    }}>
      {/* Collapsed row - always visible */}
      <div
        onClick={onToggle}
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '10px 12px',
          cursor: 'pointer',
          background: hasResult ? 'rgba(34, 197, 94, 0.05)' : 'transparent'
        }}
      >
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <span style={{ fontWeight: hasResult && fight.winner === fight.fighter1_name ? '700' : '400' }}>
              {fight.fighter1_name}
            </span>
            <span style={{ color: 'var(--text-muted)', fontSize: '13px' }}>vs</span>
            <span style={{ fontWeight: hasResult && fight.winner === fight.fighter2_name ? '700' : '400' }}>
              {fight.fighter2_name}
            </span>
            {hasResult && (
              <span style={{
                background: fight.winner === 'Draw' ? 'rgba(156, 163, 175, 0.2)' : 'rgba(34, 197, 94, 0.2)',
                color: fight.winner === 'Draw' ? '#9ca3af' : '#22c55e',
                padding: '2px 8px',
                borderRadius: '10px',
                fontSize: '11px',
                fontWeight: '500'
              }}>
                {fight.winner === 'Draw' ? (fight.method === 'NC' ? 'NC' : 'Draw') : (fight.method || 'Winner set')}
              </span>
            )}
          </div>
          <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}>
            {fight.weight_class}
          </div>
        </div>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
          style={{ transition: 'transform 0.2s', transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)', color: 'var(--text-muted)', flexShrink: 0 }}>
          <polyline points="6 9 12 15 18 9"></polyline>
        </svg>
      </div>

      {/* Expanded content */}
      {expanded && (
        <div style={{ padding: '12px', borderTop: '1px solid var(--border)', background: 'var(--bg-dark)' }}>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
            <select
              className="form-input"
              value={winner}
              onChange={(e) => setWinner(e.target.value)}
              style={{ width: 'auto', minWidth: '140px', fontSize: '14px' }}
            >
              <option value="">Select Winner</option>
              <option value={fight.fighter1_name}>{fight.fighter1_name}</option>
              <option value={fight.fighter2_name}>{fight.fighter2_name}</option>
              <option value="Draw">Draw</option>
            </select>

            {requireMethod && !isDraw && (
              <select
                className="form-input"
                value={method}
                onChange={(e) => setMethod(e.target.value)}
                style={{ width: 'auto', minWidth: '120px', fontSize: '14px' }}
              >
                <option value="">Method</option>
                {METHODS.map(m => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
            )}

            <button
              className="btn btn-primary"
              onClick={handleSave}
              disabled={!winner || (!isDraw && requireMethod && !method)}
              style={{ padding: '6px 16px', fontSize: '13px' }}
            >
              Save
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default Admin;
